import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { gsap } from "gsap";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion, AnimatePresence } from "framer-motion";
import SVGHamburger from "../components/SVGHamburger";
import { Link, useNavigate } from "react-router-dom";
import {
  ReactFlow,
  Background,
  applyNodeChanges,
  applyEdgeChanges,
  Handle,
  Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import "../styles/reactflow-override.css";
import styles from "../styles/landing.module.css";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { trackInitiateCheckout } from "../utils/metaPixel";
import { trackTikTokInitiateCheckout } from "../utils/tiktokPixel";
import { LogOut } from "react-feather";
import { FaArrowRight } from "react-icons/fa";
import {
  FiLayout,
  FiCheck,
  FiZap,
  FiList,
  FiClock,
  FiX,
  FiArrowRight,
} from "react-icons/fi";
import ModernDemo from "../components/ModernDemo";
import {
  EssayWritingDemo,
  FocusSpotifyDemo,
  MiniChatDemo,
  CommandPaletteDemo,
} from "../components/LoopingDemos";
import LiquidBackground from "../components/LiquidBackground";
import {
  FaBrain,
  FaRobot,
  FaMagic,
  FaMicrophone,
  FaPencilAlt,
} from "react-icons/fa";

const MotionLink = motion(Link);

// Register GSAP plugins after all imports (to satisfy import/first)
gsap.registerPlugin(MotionPathPlugin);
gsap.registerPlugin(ScrollTrigger);

// Feature flag: disable the single-frame global SVG backdrop so visuals are spread per-section
const SHOW_GLOBAL_BACKDROP = false;

// Memoized data
const capabilities = [
  {
    icon: <FaMagic />,
    title: "Side-by-Side PDF Chat",
    description:
      "Open two PDFs side by side and chat with both at once. Highlight a passage, ask a question, get an instant answer.",
    features: [
      "Dual-pane PDF viewer with sync scroll",
      "Highlight any text and ask Lumi instantly",
      "Cross-document comparisons and analysis",
      "Extract key points, definitions, or arguments",
    ],
  },
  {
    icon: <FaPencilAlt />,
    title: "AI-Powered Notes",
    description:
      "Take notes that think back. Lumi auto-summarizes, tags, and connects your notes to your uploaded materials.",
    features: [
      "Rich-text notes with inline AI commands",
      "Auto-summary & key-point extraction",
      "Smart tagging and backlink organization",
      "Generate flashcards & quizzes from any note",
    ],
  },
  {
    icon: <FaRobot />,
    title: "Quizzes, Flashcards & More",
    description:
      "Turn any material into a personalized study session in one click. Spaced repetition built in.",
    features: [
      "Auto-generate quizzes from docs or notes",
      "Spaced-repetition flashcard engine",
      "One-click summaries and outlines",
      "Track mastery per topic over time",
    ],
  },
];

const stats = [
  {
    value: "297K+",
    label: "Study Sessions",
    description: "Successful learning conversations with Lumi AI",
  },
  {
    value: "94%",
    label: "Comprehension Boost",
    description: "Average improvement in understanding complex topics",
  },
  {
    value: "∞",
    label: "Knowledge Access",
    description: "Unlimited questions, unlimited learning potential",
  },
];

const testimonials = [
  {
    quote:
      "I used to spend hours taking notes that made no sense two weeks later. Now every note is tied to where it came from and I actually know my material come exam day.",
    author: "Sarah Chen",
    title: "Biomedical Engineering at Johns Hopkins",
    initial: "S",
  },
  {
    quote:
      "I was the kind of student who'd read the same chapter three times and still blank on the exam. Turns out I was reviewing, not learning. Bramlo showed me the difference.",
    author: "Marcus Rodriguez",
    title: "Computer Science at Stanford",
    initial: "M",
  },
  {
    quote:
      "I never asked for help because I felt like everyone else understood and I was the only one who didn't. Lumi lets me ask the same question six different ways until it actually clicks. No embarrassment.",
    author: "Aisha Patel",
    title: "Pre-Med at Columbia",
    initial: "A",
  },
  {
    quote:
      "Before Bramlo I was juggling ChatGPT, Notion, and Quizlet and still falling behind. Now everything's in one place and my notes actually lead somewhere.",
    author: "Yuki Tanaka",
    title: "Business Analytics at NYU",
    initial: "Y",
  },
  {
    quote:
      "I started using Bramlo the week before finals. Uploaded three weeks of notes. It built a complete study plan I could actually finish in two days. I passed everything.",
    author: "Emma Thompson",
    title: "Psychology at UCLA",
    initial: "E",
  },
  {
    quote:
      "I stopped dreading study sessions once I had a real system. Bramlo built mine in about 20 minutes. I'm not smarter — I just stopped doing it the hard way.",
    author: "Alex Kim",
    title: "Software Engineering at Waterloo",
    initial: "A",
  },
  {
    quote:
      "Every essay I handed in last semester had citations I could actually trace back. My professor commented on my sourcing for the first time ever.",
    author: "Isabella Martinez",
    title: "History at Berkeley",
    initial: "I",
  },
  {
    quote:
      "I genuinely used to feel paralyzed before big assignments — didn't know where to start. Bramlo breaks everything down into steps that actually make sense for my specific material.",
    author: "David Park",
    title: "Economics at University of Chicago",
    initial: "D",
  },
  {
    quote:
      "I'm finishing my degree while working full-time. I don't have hours to re-read textbooks. Lumi reads them for me and tells me exactly what to focus on.",
    author: "Lisa Johnson",
    title: "Marketing Manager & Data Science Student",
    initial: "L",
  },
  {
    quote:
      "My son used to give up the moment something got hard — just shut down. Lumi meets him exactly where he is, asks the right questions back, and his confidence changed completely.",
    author: "Michael Torres",
    title: "Parent of High School Student",
    initial: "M",
  },
];

const faq = [
  {
    question:
      "Will Lumi actually know my specific material — or just give me generic answers?",
    answer:
      "Lumi reads your uploaded files — your lecture notes, textbook PDFs, slides, and recordings — and answers based on what's in them. When you ask a question, it cites the exact paragraph it pulled from. No hallucinated facts, no generic textbook answers. It's the difference between an AI that knows the subject and one that actually knows your class.",
  },
  {
    question: "What can I upload?",
    answer:
      "PDF textbooks, lecture slides, handwritten notes (we'll convert them), Word documents, PowerPoint files, images of whiteboard notes, and lecture recordings. Bramlo processes everything and makes it all searchable, askable, and study-ready — in one workspace.",
  },
  {
    question: "Does it work for any subject?",
    answer:
      "Yes — from high school biology to graduate-level economics. Lumi adapts to your level and your material. If it's in your uploaded files, Lumi can explain it, quiz you on it, and help you write about it. It works best when you give it your actual class material rather than asking generic questions.",
  },
  {
    question: "Is my data private?",
    answer:
      "Your files and conversations are encrypted, never shared, and never used to train our AI. What you upload belongs to you — and you can delete it anytime. We built this to be a private space where you can ask anything without judgment, and that means real privacy too.",
  },
  {
    question: "Why did you build this?",
    answer:
      "Because too many capable students fail in silence. Not because they're not smart — because they were too afraid to ask for help, had no real system for organizing what they were learning, or just didn't know where to start. We built Bramlo to be the study partner that never judges you, never gets impatient, and never makes you feel behind for asking. Every student deserves to actually understand what they're studying.",
  },
  {
    question: "How is this different from just using ChatGPT?",
    answer:
      "ChatGPT doesn't know your syllabus, your notes, or your professor's rubric. It answers from the internet — not from your material. Bramlo reads your actual files, connects your notes to your essays to your quizzes, and gives you answers you can trace back to the source. It's not a generic AI. It's a study system built around your specific class.",
  },
];

const heroOutcomeMessages = [
  "Ask Lumi anything. No judgment. Ever.",
  "Turn one chapter into a complete quiz in under a minute",
  "Every note stays tied to where it came from",
  "The study session you wish you'd had all semester",
];

const rescueScenes = [
  {
    label: "Step 1",
    title: "Stop starting from scratch every class",
    subtitle:
      "Drop in your notes, slides, and PDFs once. Lumi builds a study workspace from them — organized by topic, ready to use.",
    chip: "Workspace mapped",
    color: "#5b6df5",
    glow: "rgba(91,109,245,0.24)",
    moduleProgress: [22, 48, 64, 81],
    unitLabels: [
      "Lecture concepts",
      "Core definitions",
      "Pathway map",
      "Review queue",
    ],
    artifacts: [
      { label: "Sources indexed", value: "Lecture_6.pdf + Notes_week4.docx" },
      {
        label: "Key concepts extracted",
        value: "Cell signaling, enzyme kinetics, ATP cycle",
      },
      {
        label: "Study units created",
        value: "4 linked units with source-backed references",
      },
    ],
    transcript:
      "Your materials were organized into source-linked study units for this week.",
  },
  {
    label: "Step 2",
    title: "Find out exactly where you're weak",
    subtitle:
      "Lumi builds quizzes from what you've actually studied and zeros in on the gaps — so you stop reviewing things you already know.",
    chip: "Quiz running",
    color: "#2d9bd7",
    glow: "rgba(45,155,215,0.24)",
    moduleProgress: [34, 59, 72, 86],
    unitLabels: [
      "Recall checks",
      "Weak concepts",
      "Error patterns",
      "Retry set",
    ],
    artifacts: [
      { label: "Quiz format", value: "8 short-answer + 6 MCQ generated" },
      { label: "Weak area focus", value: "Enzyme regulation prioritized" },
      { label: "Feedback loop", value: "Hints added after each attempt" },
    ],
    transcript:
      "Current confidence improved after 8 questions with targeted feedback.",
  },
  {
    label: "Step 3",
    title: "Write with your sources already cited",
    subtitle:
      "Lumi drafts paragraphs grounded in your own material, with citations attached to every claim. You edit. You understand. You submit.",
    chip: "Draft ready",
    color: "#29b06d",
    glow: "rgba(41,176,109,0.2)",
    moduleProgress: [46, 66, 79, 91],
    unitLabels: [
      "Claim map",
      "Evidence links",
      "Draft sections",
      "Citation pass",
    ],
    artifacts: [
      { label: "Draft output", value: "1 intro + 2 body sections generated" },
      { label: "Citation links", value: "5 source anchors attached to claims" },
      { label: "Style target", value: "Academic tone with concise paragraphs" },
    ],
    transcript:
      "Draft paragraph added with source references linked back to your notes.",
  },
  {
    label: "Step 4",
    title: "Walk in knowing you're ready",
    subtitle:
      "One final pass: recap cards, weak-spot review, and a timed exam sequence — all built from your own notes. No more hoping it clicks.",
    chip: "Review complete",
    color: "#cf7a2a",
    glow: "rgba(207,122,42,0.2)",
    moduleProgress: [61, 76, 88, 96],
    unitLabels: [
      "Final recap",
      "Weak-topic sprint",
      "Timed practice",
      "Exam readiness",
    ],
    artifacts: [
      {
        label: "Final checklist",
        value: "Recap done, 2 topics flagged for review",
      },
      {
        label: "Memory pass",
        value: "Flashcards scheduled for tonight + morning",
      },
      { label: "Exam plan", value: "90-min sequence with priority topics" },
    ],
    transcript:
      "Review checklist complete and weak topics queued for one more pass.",
  },
];

/* ── Capabilities conversion demo ─────────────────────────────── */
const CapConversionDemo = () => {
  const rootRef = useRef(null);

  const NOTES = [
    {
      num: "12.1",
      title: "Mendelian Inheritance",
      body: "Dominant alleles mask recessive ones — a single dominant copy is enough to express the phenotype.",
    },
    {
      num: "12.2",
      title: "DNA Structure",
      body: "The double helix pairs A–T and C–G. Complementary base-pairing enables faithful replication.",
    },
    {
      num: "12.3",
      title: "Gene Expression",
      body: "Transcription copies DNA into mRNA; translation reads each codon to assemble the protein.",
    },
  ];

  const PDF_TOP = [100, 86, 93, 78, 90];
  const PDF_BTM = [95, 72, 84, 88, 60, 76, 91, 67];

  const QS = [
    "A Yy plant self-pollinates. What is the expected phenotypic ratio, and why?",
    "Why does mRNA use uracil in place of thymine during transcription?",
    "How does a frameshift mutation alter protein structure vs. a point mutation?",
  ];

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    /* ── selectors ── */
    const heads = el.querySelectorAll("[data-ccd-head]");
    const title = el.querySelector("[data-ccd-title]");
    const props = el.querySelectorAll("[data-ccd-prop]");
    const divider = el.querySelector("[data-ccd-divider]");
    const nums = el.querySelectorAll("[data-ccd-num]");
    const sections = el.querySelectorAll("[data-ccd-note]");
    const cursor = el.querySelector("[data-ccd-cursor]");
    const pdfHead = el.querySelector("[data-ccd-pdfhead]");
    const lines = el.querySelectorAll("[data-ccd-line]");
    const hlBg = el.querySelector("[data-ccd-hlbg]");
    const hlText = el.querySelector("[data-ccd-hltext]");
    const hlLink = el.querySelector("[data-ccd-hllink]");
    const chips = el.querySelectorAll("[data-ccd-chip]");
    const qs = el.querySelectorAll("[data-ccd-q]");
    const rdBox = el.querySelector("[data-ccd-rd]");
    const rdFill = el.querySelector("[data-ccd-fill]");
    const startBtn = el.querySelector("[data-ccd-start]");

    /* ── initial hidden states ── */
    gsap.set(heads, { opacity: 0, y: -10 });
    gsap.set(title, { opacity: 0, y: 14 });
    gsap.set(props, { opacity: 0, scale: 0.75 });
    gsap.set(divider, { scaleX: 0, transformOrigin: "left center" });
    gsap.set(nums, { opacity: 0, scale: 0.5 });
    gsap.set(sections, { opacity: 0, y: 14 });
    gsap.set(cursor, { opacity: 0 });
    gsap.set(pdfHead, { opacity: 0 });
    gsap.set(lines, { scaleX: 0, transformOrigin: "left center" });
    gsap.set(hlBg, { scaleX: 0, transformOrigin: "left center" });
    gsap.set(hlText, { opacity: 0, y: 8 });
    gsap.set(hlLink, { opacity: 0, x: -8 });
    gsap.set(chips, { opacity: 0, scale: 0.75 });
    gsap.set(qs, { opacity: 0, x: 20 });
    gsap.set(rdBox, { opacity: 0, y: 12 });
    gsap.set(startBtn, { opacity: 0, scale: 0.9 });

    let tl;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();

        tl = gsap.timeline({ delay: 0.15 });

        /* 1 — column headers drop in */
        tl.to(heads, {
          opacity: 1,
          y: 0,
          duration: 0.42,
          stagger: 0.1,
          ease: "power2.out",
        });

        /* 2 — notes: title rises */
        tl.to(
          title,
          { opacity: 1, y: 0, duration: 0.46, ease: "power3.out" },
          "-=0.22",
        );

        /* 3 — notes: prop tags pop */
        tl.to(
          props,
          {
            opacity: 1,
            scale: 1,
            duration: 0.32,
            stagger: 0.09,
            ease: "back.out(2)",
          },
          "-=0.24",
        );

        /* 4 — divider draws */
        tl.to(
          divider,
          { scaleX: 1, duration: 0.4, ease: "power2.inOut" },
          "-=0.1",
        );

        /* 5 — section num badges bounce in + sections fade up (same beat) */
        tl.to(
          nums,
          {
            opacity: 1,
            scale: 1,
            duration: 0.42,
            stagger: 0.17,
            ease: "back.out(2.5)",
          },
          "-=0.08",
        );
        tl.to(
          sections,
          {
            opacity: 1,
            y: 0,
            duration: 0.46,
            stagger: 0.17,
            ease: "power3.out",
          },
          "<",
        );

        /* 6 — cursor blinks on after last section */
        tl.set(cursor, { opacity: 1 }, "+=0.06");

        /* 7 — PDF header + lines scan in (overlaps with notes anim) */
        tl.to(
          pdfHead,
          { opacity: 1, duration: 0.3, ease: "power2.out" },
          "<-=0.6",
        );
        tl.to(
          lines,
          { scaleX: 1, duration: 0.2, stagger: 0.016, ease: "power2.out" },
          "-=0.26",
        );

        /* 8 — highlight block sweeps in */
        tl.to(
          hlBg,
          { scaleX: 1, duration: 0.52, ease: "power3.inOut" },
          "+=0.04",
        );
        tl.to(
          hlText,
          { opacity: 1, y: 0, duration: 0.38, ease: "power2.out" },
          "-=0.28",
        );
        tl.to(
          hlLink,
          { opacity: 1, x: 0, duration: 0.32, ease: "power2.out" },
          "-=0.1",
        );

        /* 9 — quiz: source chips pop */
        tl.to(
          chips,
          {
            opacity: 1,
            scale: 1,
            duration: 0.3,
            stagger: 0.1,
            ease: "back.out(1.8)",
          },
          "+=0.14",
        );

        /* 10 — questions slide in from right, one by one */
        tl.to(
          qs,
          {
            opacity: 1,
            x: 0,
            duration: 0.52,
            stagger: 0.28,
            ease: "power3.out",
          },
          "-=0.08",
        );

        /* 11 — readiness box rises */
        tl.to(
          rdBox,
          { opacity: 1, y: 0, duration: 0.38, ease: "power3.out" },
          "+=0.1",
        );

        /* 12 — bar fills with expo snap */
        tl.fromTo(
          rdFill,
          { width: "0%" },
          { width: "74%", duration: 1.0, ease: "expo.out" },
          "-=0.22",
        );

        /* 13 — start button scale-pops */
        tl.to(
          startBtn,
          { opacity: 1, scale: 1, duration: 0.48, ease: "back.out(1.6)" },
          "-=0.55",
        );

        /* 14 — post-intro: start button breathes gently */
        tl.to(
          startBtn,
          {
            boxShadow:
              "0 0 28px rgba(99,102,241,0.55), 0 0 0 1px rgba(129,140,248,0.6)",
            duration: 1.3,
            yoyo: true,
            repeat: -1,
            ease: "power1.inOut",
          },
          "+=0.3",
        );
      },
      { rootMargin: "80px 0px", threshold: 0.08 },
    );

    io.observe(el);
    return () => {
      io.disconnect();
      tl?.kill();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div ref={rootRef} className={styles.ccd}>
      {/* ── Title bar ── */}
      <div className={styles.ccdBar}>
        <span className={styles.ccdDot} style={{ background: "#FF5F57" }} />
        <span className={styles.ccdDot} style={{ background: "#FEBC2E" }} />
        <span className={styles.ccdDot} style={{ background: "#28C840" }} />
        <div className={styles.ccdTabs}>
          {[
            { label: "My Notes", active: true },
            { label: "Genetics.pdf", active: false },
            { label: "Practice Quiz ✓", active: false },
          ].map(({ label, active }) => (
            <span
              key={label}
              className={`${styles.ccdTab}${active ? " " + styles.ccdTabActive : ""}`}
            >
              {label}
            </span>
          ))}
        </div>
        <span className={styles.ccdBarRight}>Biology 201</span>
      </div>

      {/* ── Three-column body ── */}
      <div className={styles.ccdBody}>
        {/* COL 1 — Notes */}
        <div className={styles.ccdColNotes}>
          <div data-ccd-head className={styles.ccdColHead}>
            <span className={styles.ccdColIcon}>📝</span>
            <span className={styles.ccdColTitle}>My Notes</span>
            <span className={styles.ccdColMeta}>Ch. 12</span>
          </div>
          <div data-ccd-title className={styles.ccdH1Text}>
            Genetics &amp; Heredity
          </div>
          <div className={styles.ccdProps}>
            {["📅 Week 7", "🏷 Midterm"].map((p) => (
              <span key={p} data-ccd-prop className={styles.ccdProp}>
                {p}
              </span>
            ))}
          </div>
          <div data-ccd-divider className={styles.ccdDivider} />
          <div className={styles.ccdSections}>
            {NOTES.map(({ num, title, body }, i) => (
              <div key={i} data-ccd-note className={styles.ccdSection}>
                <span data-ccd-num className={styles.ccdSectionNum}>
                  {num}
                </span>
                <div>
                  <div className={styles.ccdSectionTitle}>{title}</div>
                  <p className={styles.ccdPara}>
                    {body}
                    {i === NOTES.length - 1 && (
                      <span data-ccd-cursor className={styles.ccdCursor} />
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* COL 2 — PDF Viewer */}
        <div className={styles.ccdColPdf}>
          <div data-ccd-head className={styles.ccdColHead}>
            <span className={styles.ccdColIcon}>📄</span>
            <span className={styles.ccdColTitle}>Genetics.pdf</span>
            <span className={styles.ccdColMeta}>p. 47</span>
          </div>
          <div className={styles.ccdPdfPage}>
            <div data-ccd-pdfhead className={styles.ccdPdfChapter}>
              Chapter 12 — Genetics &amp; Heredity
            </div>
            {PDF_TOP.map((w, i) => (
              <div
                key={`t${i}`}
                data-ccd-line
                className={styles.ccdPdfLine}
                style={{ width: `${w}%` }}
              />
            ))}
            <div className={styles.ccdPdfHl}>
              <div data-ccd-hlbg className={styles.ccdPdfHlBg} />
              <p data-ccd-hltext className={styles.ccdPdfHlText}>
                "Mendel observed that traits are inherited as discrete units,
                each allele contributed independently by each parent organism."
              </p>
              <span data-ccd-hllink className={styles.ccdPdfHlLink}>
                ↗ Linked to 12.1 in your notes
              </span>
            </div>
            {PDF_BTM.map((w, i) => (
              <div
                key={`b${i}`}
                data-ccd-line
                className={styles.ccdPdfLine}
                style={{ width: `${w}%` }}
              />
            ))}
          </div>
        </div>

        {/* COL 3 — Practice Quiz */}
        <div className={styles.ccdColQuiz}>
          <div data-ccd-head className={styles.ccdColHead}>
            <span className={styles.ccdColIcon}>📋</span>
            <span className={styles.ccdColTitle}>Practice Quiz</span>
            <span className={styles.ccdPanelDone}>✓ 3 Qs</span>
          </div>
          <div className={styles.ccdRef}>
            <span className={styles.ccdRefLabel}>Sources</span>
            {["Ch. 12 notes", "pp. 44–61"].map((c) => (
              <span key={c} data-ccd-chip className={styles.ccdRefChip}>
                {c}
              </span>
            ))}
          </div>
          <div className={styles.ccdQList}>
            {QS.map((q, i) => (
              <div key={i} data-ccd-q className={styles.ccdQ}>
                <span className={styles.ccdQN}>Q{i + 1}</span>
                <span className={styles.ccdQText}>{q}</span>
              </div>
            ))}
          </div>
          <div data-ccd-rd className={styles.ccdReadiness}>
            <div className={styles.ccdReadinessRow}>
              <span className={styles.ccdReadinessLabel}>Exam readiness</span>
              <span className={styles.ccdReadinessScore}>74%</span>
            </div>
            <div className={styles.ccdReadinessBar}>
              <div data-ccd-fill className={styles.ccdReadinessFill} />
            </div>
          </div>
          <div data-ccd-start className={styles.ccdStartBtn}>
            Start Practice Exam →
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Animated stat counter ────────────────────────────────────── */
const StatCounter = ({ value, className }) => {
  const elRef = useRef(null);
  useEffect(() => {
    const el = elRef.current;
    if (!el || value === "∞") return;
    const m = value.match(/^([\d.]+)(.*)/);
    if (!m) return;
    const target = parseFloat(m[1]);
    const suffix = m[2] || "";
    const obj = { v: 0 };
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        gsap.to(obj, {
          v: target,
          duration: 1.85,
          delay: 0.5,
          ease: "power2.out",
          onUpdate() {
            el.textContent = Math.round(obj.v) + suffix;
          },
          onComplete() {
            el.textContent = value;
          },
        });
      },
      { rootMargin: "60px 0px", threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <span ref={elRef} className={className}>
      {value}
    </span>
  );
};

/* ── Cap trio card footer micro-demos ─────────────────────────── */
const ChatCardFooter = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const badges = el.querySelectorAll("[data-cap-badge]");
    const bubble = el.querySelector("[data-cap-bubble]");
    const reply = el.querySelector("[data-cap-reply]");
    gsap.set(badges, { opacity: 0, y: -10, scale: 0.82 });
    gsap.set(bubble, { opacity: 0, y: 10 });
    gsap.set(reply, { opacity: 0, y: 12 });
    let tl;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        tl = gsap.timeline({ delay: 0.65 });
        tl.to(badges, {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.38,
          stagger: 0.13,
          ease: "back.out(2.2)",
        });
        tl.to(
          bubble,
          { opacity: 1, y: 0, duration: 0.4, ease: "power3.out" },
          "+=0.12",
        );
        tl.to(
          reply,
          { opacity: 1, y: 0, duration: 0.4, ease: "power3.out" },
          "+=0.18",
        );
      },
      { rootMargin: "40px 0px", threshold: 0.45 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      tl?.kill();
    };
  }, []);
  return (
    <div ref={ref} className={styles.capTrioChatSnip}>
      <div className={styles.capTrioPDFRefRow}>
        <span data-cap-badge className={styles.capTrioPDFBadge}>
          Chapter_7.pdf
        </span>
        <span data-cap-badge className={styles.capTrioPDFBadge}>
          Lecture_Notes.pdf
        </span>
      </div>
      <div data-cap-bubble className={styles.capTrioChatBubble}>
        Compare the two definitions of entropy
      </div>
      <div data-cap-reply className={styles.capTrioChatReply}>
        <span className={styles.capTrioReplyBadge}>Lumi</span>
        Chapter 7 frames it as disorder; your notes describe it as energy
        unavailability. Both are valid...
      </div>
    </div>
  );
};

const NotesCardFooter = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const title = el.querySelector("[data-cap-ntitle]");
    const body = el.querySelector("[data-cap-nbody]");
    const chip = el.querySelector("[data-cap-naichip]");
    const tags = el.querySelectorAll("[data-cap-ntag]");
    gsap.set(title, { opacity: 0, x: -10 });
    gsap.set(body, { opacity: 0 });
    gsap.set(chip, { opacity: 0, scale: 0.78 });
    gsap.set(tags, { opacity: 0, scale: 0.72 });
    let tl;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        tl = gsap.timeline({ delay: 0.7 });
        tl.to(title, { opacity: 1, x: 0, duration: 0.4, ease: "power3.out" });
        tl.to(
          body,
          { opacity: 1, duration: 0.36, ease: "power2.out" },
          "-=0.1",
        );
        tl.to(
          chip,
          { opacity: 1, scale: 1, duration: 0.38, ease: "back.out(1.9)" },
          "+=0.08",
        );
        tl.to(
          tags,
          {
            opacity: 1,
            scale: 1,
            duration: 0.3,
            stagger: 0.09,
            ease: "back.out(2.2)",
          },
          "+=0.06",
        );
      },
      { rootMargin: "40px 0px", threshold: 0.45 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      tl?.kill();
    };
  }, []);
  return (
    <div ref={ref} className={styles.capTrioNotesPreview}>
      <div data-cap-ntitle className={styles.capTrioNoteTitle}>
        Lecture 3 — Cell Biology
      </div>
      <div data-cap-nbody className={styles.capTrioNoteBody}>
        Mitosis divides somatic cells into two genetically identical daughter
        cells...
      </div>
      <div data-cap-naichip className={styles.capTrioAiChip}>
        ⚡ Auto-summarized · 4 key points extracted
      </div>
      <div className={styles.capTrioTagsSnip}>
        {["Mitosis", "Cell Cycle", "Exam"].map((t) => (
          <span key={t} data-cap-ntag className={styles.capTrioTag}>
            {t}
          </span>
        ))}
      </div>
    </div>
  );
};

const QuizCardFooter = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const meta = el.querySelector("[data-cap-qmeta]");
    const fill = el.querySelector("[data-cap-qfill]");
    const pill = el.querySelector("[data-cap-qpill]");
    gsap.set(meta, { opacity: 0 });
    gsap.set(fill, { width: "0%" });
    gsap.set(pill, { opacity: 0, scale: 0.82 });
    let tl;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        tl = gsap.timeline({ delay: 0.75 });
        tl.to(meta, { opacity: 1, duration: 0.32, ease: "power2.out" });
        tl.to(
          fill,
          { width: "67%", duration: 0.95, ease: "expo.out" },
          "+=0.08",
        );
        tl.to(
          pill,
          { opacity: 1, scale: 1, duration: 0.38, ease: "back.out(1.7)" },
          "-=0.35",
        );
      },
      { rootMargin: "40px 0px", threshold: 0.45 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      tl?.kill();
    };
  }, []);
  return (
    <div ref={ref} className={styles.capTrioQuizSnip}>
      <div data-cap-qmeta className={styles.capTrioQuizMeta}>
        Quiz · 8 / 12 correct
      </div>
      <div className={styles.capTrioQuizBar}>
        <div data-cap-qfill className={styles.capTrioQuizBarFill} />
      </div>
      <div data-cap-qpill className={styles.capTrioQuizPill}>
        ✓ Spaced repetition active
      </div>
    </div>
  );
};

/* ── "One app, three tools" animated list ────────────────────── */
const TRI_APP_ITEMS = [
  {
    replaces: "ChatGPT",
    Icon: FaBrain,
    title: "A tutor that actually read your notes",
    desc: "Ask anything. Every answer cites the exact paragraph it came from — one click to jump back and verify.",
  },
  {
    replaces: "Notion",
    Icon: FaMicrophone,
    title: "Your whole semester, instantly askable",
    desc: "Drop in PDFs, paste lecture notes, record in class. Upload once — search, summarize, and chat forever.",
  },
  {
    replaces: "Quizlet",
    Icon: FaPencilAlt,
    title: "Readings to test-ready in 10 seconds",
    desc: "Flashcards, quizzes, cited essay drafts — one click each. Spaced repetition schedules the rest for you.",
  },
];

const TriAppList = ({ active, onSetActive }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rows = el.querySelectorAll("[data-tri-row]");
    const icons = el.querySelectorAll("[data-tri-icon]");
    const titles = el.querySelectorAll("[data-tri-title]");
    const replaces = el.querySelectorAll("[data-tri-replaces]");
    const descs = el.querySelectorAll("[data-tri-desc]");

    gsap.set(rows, { opacity: 0, x: -22 });
    gsap.set(icons, { opacity: 0, scale: 0.55 });
    gsap.set(titles, { opacity: 0, y: 6 });
    gsap.set(replaces, { opacity: 0 });
    gsap.set(descs, { opacity: 0 });

    let tl;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        tl = gsap.timeline({ delay: 0.3 });
        tl.to(rows, {
          opacity: 1,
          x: 0,
          duration: 0.52,
          stagger: 0.15,
          ease: "power3.out",
        });
        tl.to(
          icons,
          {
            opacity: 1,
            scale: 1,
            duration: 0.38,
            stagger: 0.15,
            ease: "back.out(2.2)",
          },
          "<+0.06",
        );
        tl.to(
          titles,
          {
            opacity: 1,
            y: 0,
            duration: 0.36,
            stagger: 0.15,
            ease: "power2.out",
          },
          "<+0.08",
        );
        tl.to(
          replaces,
          { opacity: 1, duration: 0.3, stagger: 0.15, ease: "power2.out" },
          "<+0.06",
        );
        tl.to(
          descs,
          { opacity: 1, duration: 0.4, stagger: 0.15, ease: "power2.out" },
          "<+0.1",
        );
      },
      { rootMargin: "60px 0px", threshold: 0.25 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      tl?.kill();
    };
  }, []);

  return (
    <ul ref={ref} className={styles.triAppList}>
      {TRI_APP_ITEMS.map(({ replaces, Icon, title, desc }, idx) => (
        <li
          key={replaces}
          data-tri-row
          className={`${styles.triAppRow}${active === idx ? " " + styles.triAppRowActive : ""}`}
          onClick={() => onSetActive(idx)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onSetActive(idx);
            }
          }}
        >
          <span data-tri-icon className={styles.triAppIcon}>
            <Icon size={16} />
          </span>
          <div className={styles.triAppBody}>
            <div className={styles.triAppRowHdr}>
              <span data-tri-title className={styles.triAppRowTitle}>
                {title}
              </span>
              <span data-tri-replaces className={styles.triAppReplaces}>
                replaces <em>{replaces}</em>
              </span>
            </div>
            <p data-tri-desc className={styles.triAppDesc}>
              {desc}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
};

/* ── Assignment automation feature cards ─────────────────────── */
const AUTO_FEATURES = [
  {
    title: "Reads the brief. Hits every point.",
    desc: "Lumi maps every assignment requirement to what's in your own notes before it writes a single sentence.",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
      >
        <path
          d="M12 2L2 7l10 5 10-5-10-5z"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M2 17l10 5 10-5M2 12l10 5 10-5"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    title: "Pulls from everything you've uploaded",
    desc: "Your draft draws on every source you've given it — not the internet — so the content is actually yours.",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
      >
        <path
          d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <polyline
          points="14 2 14 8 20 8"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <line
          x1="16"
          y1="13"
          x2="8"
          y2="13"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <line
          x1="16"
          y1="17"
          x2="8"
          y2="17"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    title: "Every claim traced back to the source",
    desc: "Inline citations link to the exact paragraph in your material — so you never wonder where something came from.",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
      >
        <path
          d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <line
          x1="7"
          y1="7"
          x2="7.01"
          y2="7"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
];

const AutoFeaturesGrid = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const cards = el.querySelectorAll("[data-auto-card]");
    const icons = el.querySelectorAll("[data-auto-icon]");
    const titles = el.querySelectorAll("[data-auto-title]");
    const descs = el.querySelectorAll("[data-auto-desc]");

    gsap.set(cards, { opacity: 0, y: 24 });
    gsap.set(icons, { opacity: 0, scale: 0.5 });
    gsap.set(titles, { opacity: 0, x: -10 });
    gsap.set(descs, { opacity: 0 });

    let tl;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        tl = gsap.timeline({ delay: 0.2 });
        tl.to(cards, {
          opacity: 1,
          y: 0,
          duration: 0.5,
          stagger: 0.15,
          ease: "power3.out",
        });
        tl.to(
          icons,
          {
            opacity: 1,
            scale: 1,
            duration: 0.4,
            stagger: 0.15,
            ease: "back.out(2.4)",
          },
          "<+0.05",
        );
        tl.to(
          titles,
          {
            opacity: 1,
            x: 0,
            duration: 0.38,
            stagger: 0.15,
            ease: "power2.out",
          },
          "<+0.1",
        );
        tl.to(
          descs,
          { opacity: 1, duration: 0.38, stagger: 0.15, ease: "power2.out" },
          "<+0.1",
        );
      },
      { rootMargin: "60px 0px", threshold: 0.3 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      tl?.kill();
    };
  }, []);

  return (
    <div ref={ref} className={styles.automationFeaturesCompact}>
      {AUTO_FEATURES.map(({ title, desc, icon }, i) => (
        <div key={i} data-auto-card className={styles.automationFeatureCompact}>
          <div data-auto-icon className={styles.featureIconProCompact}>
            {icon}
          </div>
          <h3
            data-auto-title
            className={`${styles.featureTitleCompact} ${styles.titleAnimateChars}`}
          >
            <span data-title-animate-chars>{title}</span>
          </h3>
          <p data-auto-desc className={styles.featureDescCompact}>
            {desc}
          </p>
        </div>
      ))}
    </div>
  );
};

/* ── "What Students Say" testimonials grid ───────────────────── */
const TestimonialsGrid = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const cards = el.querySelectorAll("[data-rev-card]");
    const stars = el.querySelectorAll("[data-rev-stars]");
    const quotes = el.querySelectorAll("[data-rev-quote]");
    const authors = el.querySelectorAll("[data-rev-author]");
    const bigQ = el.querySelector("[data-rev-bigq]");

    gsap.set(cards, { opacity: 0, y: 30 });
    gsap.set(bigQ, { opacity: 0, scale: 0.6 });
    gsap.set(stars, { opacity: 0, scale: 0.65 });
    gsap.set(quotes, { opacity: 0 });
    gsap.set(authors, { opacity: 0, y: 8 });

    let tl;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        tl = gsap.timeline({ delay: 0.2 });
        tl.to(cards, {
          opacity: 1,
          y: 0,
          duration: 0.54,
          stagger: 0.07,
          ease: "power3.out",
        });
        tl.to(
          bigQ,
          { opacity: 1, scale: 1, duration: 0.38, ease: "back.out(1.8)" },
          "<+0.05",
        );
        tl.to(
          stars,
          {
            opacity: 1,
            scale: 1,
            duration: 0.3,
            stagger: 0.06,
            ease: "back.out(2.2)",
          },
          "<+0.08",
        );
        tl.to(
          quotes,
          { opacity: 1, duration: 0.44, stagger: 0.06, ease: "power2.out" },
          "<+0.12",
        );
        tl.to(
          authors,
          {
            opacity: 1,
            y: 0,
            duration: 0.38,
            stagger: 0.06,
            ease: "power2.out",
          },
          "<+0.14",
        );
      },
      { rootMargin: "60px 0px", threshold: 0.12 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      tl?.kill();
    };
  }, []);

  return (
    <div ref={ref} className={styles.reviewGrid}>
      <div
        data-rev-card
        className={`${styles.reviewCard2} ${styles.reviewCard2Featured}`}
      >
        <div data-rev-bigq className={styles.reviewBigQMark}>
          {'"'}
        </div>
        <div data-rev-stars className={styles.reviewCard2Stars}>
          {"★★★★★"}
        </div>
        <p data-rev-quote className={styles.reviewCard2BigText}>
          {testimonials[0].quote}
        </p>
        <div data-rev-author className={styles.reviewCard2Author}>
          <div className={styles.reviewCard2Avatar}>
            {testimonials[0].initial}
          </div>
          <div>
            <div className={styles.reviewCard2Name}>
              {testimonials[0].author}
            </div>
            <div className={styles.reviewCard2Role}>
              {testimonials[0].title}
            </div>
          </div>
        </div>
      </div>
      {testimonials.slice(1, 7).map((t) => (
        <div key={t.author} data-rev-card className={styles.reviewCard2}>
          <div data-rev-stars className={styles.reviewCard2Stars}>
            {"★★★★★"}
          </div>
          <p data-rev-quote className={styles.reviewCard2Text}>
            {t.quote}
          </p>
          <div data-rev-author className={styles.reviewCard2Author}>
            <div className={styles.reviewCard2Avatar}>{t.initial}</div>
            <div>
              <div className={styles.reviewCard2Name}>{t.author}</div>
              <div className={styles.reviewCard2Role}>{t.title}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ── Spotify section feature list ────────────────────────────── */
const FLOW_FEATURES = [
  "Skip tracks, adjust volume, and queue playlists without leaving your workspace",
  "Auto-pauses when Lumi reads aloud or runs a quiz — nothing talks over anything",
  "Session timer stays synced with what you're studying, not a separate app",
  "Suggests study-mode playlists automatically — lo-fi for note-taking, ambience for writing",
];

const FlowFeaturesList = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const items = el.querySelectorAll("[data-flow-item]");
    const checks = el.querySelectorAll("[data-flow-check]");

    gsap.set(items, { opacity: 0, x: -20 });
    gsap.set(checks, { opacity: 0, scale: 0.5 });

    let tl;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        tl = gsap.timeline({ delay: 0.35 });
        tl.to(items, {
          opacity: 1,
          x: 0,
          duration: 0.5,
          stagger: 0.14,
          ease: "power3.out",
        });
        tl.to(
          checks,
          {
            opacity: 1,
            scale: 1,
            duration: 0.36,
            stagger: 0.14,
            ease: "back.out(2.4)",
          },
          "<+0.06",
        );
      },
      { rootMargin: "60px 0px", threshold: 0.3 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      tl?.kill();
    };
  }, []);

  return (
    <ul ref={ref} className={styles.flowFeatures}>
      {FLOW_FEATURES.map((text, i) => (
        <li key={i} data-flow-item>
          <span data-flow-check className={styles.flowCheck} aria-hidden>
            <FiCheck size={11} strokeWidth={3} />
          </span>
          {text}
        </li>
      ))}
    </ul>
  );
};

/* ── Savings comparison strip ────────────────────────────────── */
const SavingsStrip = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const items = el.querySelectorAll("[data-sav-item]");
    const arrow = el.querySelector("[data-sav-arrow]");
    const bramlo = el.querySelector("[data-sav-bramlo]");
    const badge = el.querySelector("[data-sav-badge]");

    gsap.set(items, { opacity: 0, x: -14 });
    gsap.set(arrow, { opacity: 0, x: -10 });
    gsap.set(bramlo, { opacity: 0, x: -10 });
    gsap.set(badge, { opacity: 0, scale: 0.75 });

    let tl;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        tl = gsap.timeline({ delay: 0.5 });
        tl.to(items, {
          opacity: 1,
          x: 0,
          duration: 0.42,
          stagger: 0.12,
          ease: "power2.out",
        });
        tl.to(
          arrow,
          { opacity: 1, x: 0, duration: 0.3, ease: "power2.out" },
          "+=0.04",
        );
        tl.to(
          bramlo,
          { opacity: 1, x: 0, duration: 0.38, ease: "power2.out" },
          "-=0.1",
        );
        tl.to(
          badge,
          { opacity: 1, scale: 1, duration: 0.38, ease: "back.out(2)" },
          "-=0.2",
        );
      },
      { rootMargin: "60px 0px", threshold: 0.5 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      tl?.kill();
    };
  }, []);

  return (
    <div ref={ref} className={styles.aiSavingsRow}>
      {[
        { name: "ChatGPT Plus", price: "$20" },
        { name: "Notion", price: "$8" },
        { name: "Quizlet", price: "$8" },
      ].map((item, i) => (
        <span key={item.name} data-sav-item className={styles.aiSavingsItem}>
          {i > 0 && <span className={styles.aiSavingsPlus}>+</span>}
          <span className={styles.aiSavingsName}>{item.name}</span>
          <span className={styles.aiSavingsPrice}>{item.price}/mo</span>
        </span>
      ))}
      <span data-sav-arrow className={styles.aiSavingsArrow}>
        →
      </span>
      <span data-sav-bramlo className={styles.aiSavingsBramlo}>
        Bramlo&nbsp;<strong>$10/mo</strong>
      </span>
      <span data-sav-badge className={styles.aiSavingsBadge}>
        Save $26/mo
      </span>
    </div>
  );
};

const LandingPage = () => {
  /** True when backdrop-filter:url(#…) is unsupported — use blur-only navbar (Firefox, etc.). */
  const navLiquidBackdropUnsupported = useMemo(() => {
    if (typeof window === "undefined") return false;
    const probe = document.createElement("div");
    probe.style.cssText =
      "position:absolute;visibility:hidden;left:-9999px;top:0;backdrop-filter:url(#bramloNavbarLiquidGlass)";
    document.body.appendChild(probe);
    const parsed = getComputedStyle(probe).backdropFilter || "";
    document.body.removeChild(probe);
    /* Empty or no url() token ⇒ Firefox / engines without liquid backdrop */
    return !parsed.trim() || !parsed.includes("url");
  }, []);

  // Ensure GSAP plugins are registered for animations used across this file
  gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const faqContentRefs = useRef([]);
  const demoRef = useRef(null);
  const [showDemo, setShowDemo] = useState(false);
  const featuresRef = useRef(null);
  const pricingRef = useRef(null);
  const backdropRef = useRef(null);
  const { isAuthenticated, signOut } = useAuth();
  const navigate = useNavigate();

  // Handle plan selection - FREE VERSION: just navigate to auth
  const foundingOfferLink = "https://buy.stripe.com/fZu7sMcmlaxb5CP1I1cQU08";
  const handlePlanClick = (planId) => {
    const stripeLinks = {
      monthly: foundingOfferLink,
      annual: "https://buy.stripe.com/eVq9AU4TT34J1mzcmFcQU0f",
    };

    if (planId === "enterprise") {
      navigate("/contact");
      return;
    }

    if (!isAuthenticated) {
      toast.info("Create your free account to continue to checkout");
      navigate("/auth", {
        state: { planId, returnTo: `/pricing?plan=${planId}` },
      });
      return;
    }

    const pixelPlanId =
      planId === "annual" ? "premium_annual" : "premium_monthly";
    trackInitiateCheckout(pixelPlanId);
    trackTikTokInitiateCheckout(pixelPlanId);
    const url = stripeLinks[planId] || stripeLinks.monthly;
    setTimeout(() => {
      window.location.href = url;
    }, 300);
  };
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuContentRef = useRef(null);
  const [heroOutcomeIndex, setHeroOutcomeIndex] = useState(0);
  const [useSimpleHeroSignal, setUseSimpleHeroSignal] = useState(false);
  const heroSignalLettersRef = useRef([]);
  const heroSignalAnimRef = useRef(null);
  const [rescueSceneIndex, setRescueSceneIndex] = useState(0);

  // Sticky bottom CTA: reveals after user scrolls past 35% of page.
  const [stickyCtaOpen, setStickyCtaOpen] = useState(false);
  const [stickyCtaDismissed, setStickyCtaDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.sessionStorage.getItem("Bramlo_sticky_cta_dismissed") === "1";
  });
  useEffect(() => {
    const onScroll = () => {
      if (stickyCtaDismissed) return;
      const { scrollTop, scrollHeight, clientHeight } =
        document.documentElement;
      const pct = scrollTop / Math.max(1, scrollHeight - clientHeight);
      // Show once user is 35%-90% through the page (hide at footer)
      setStickyCtaOpen(pct > 0.35 && pct < 0.9);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [stickyCtaDismissed]);
  const dismissStickyCta = () => {
    setStickyCtaOpen(false);
    setStickyCtaDismissed(true);
    try {
      window.sessionStorage.setItem("Bramlo_sticky_cta_dismissed", "1");
    } catch (e) {
      /* ignore */
    }
  };

  // Tri-app card: which capability is showcased in the visual panel.
  const [triAppActive, setTriAppActive] = useState(0);
  const [triAppPaused, setTriAppPaused] = useState(false);
  useEffect(() => {
    if (triAppPaused) return undefined;
    const t = setInterval(() => {
      setTriAppActive((i) => (i + 1) % 3);
    }, 5200);
    return () => clearInterval(t);
  }, [triAppPaused]);

  /* Hero outcome line: exit old phrase (staggered), then swap text, then enter new phrase */
  useEffect(() => {
    const media = window.matchMedia("(max-width: 768px)");
    const update = () => setUseSimpleHeroSignal(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const cycleMs = 4800;
    const intervalId = window.setInterval(() => {
      if (useSimpleHeroSignal) {
        setHeroOutcomeIndex((prev) => (prev + 1) % heroOutcomeMessages.length);
        return;
      }
      const letters = heroSignalLettersRef.current.filter(Boolean);
      const next = () => {
        setHeroOutcomeIndex((prev) => (prev + 1) % heroOutcomeMessages.length);
      };
      if (!letters.length) {
        next();
        return;
      }
      heroSignalAnimRef.current?.kill();
      heroSignalAnimRef.current = gsap.to(letters, {
        opacity: 0,
        y: -11,
        rotateX: 72,
        filter: "blur(5px)",
        duration: 0.44,
        ease: "power2.in",
        stagger: { each: 0.012, from: "random" },
        transformOrigin: "50% 100%",
        onComplete: next,
      });
    }, cycleMs);
    return () => {
      window.clearInterval(intervalId);
      heroSignalAnimRef.current?.kill();
    };
  }, [useSimpleHeroSignal]);

  useLayoutEffect(() => {
    if (useSimpleHeroSignal) return undefined;
    heroSignalAnimRef.current?.kill();
    const run = () => {
      const letters = heroSignalLettersRef.current.filter(Boolean);
      if (!letters.length) return;
      gsap.set(letters, {
        transformOrigin: "50% 100%",
        opacity: 0,
        y: 14,
        rotateX: -78,
        filter: "blur(5px)",
      });
      heroSignalAnimRef.current = gsap.to(letters, {
        opacity: 1,
        y: 0,
        rotateX: 0,
        filter: "blur(0px)",
        duration: 0.58,
        ease: "power3.out",
        stagger: { each: 0.02, from: "random" },
        overwrite: "auto",
      });
    };
    const id = requestAnimationFrame(run);
    return () => {
      cancelAnimationFrame(id);
      heroSignalAnimRef.current?.kill();
    };
  }, [heroOutcomeIndex, useSimpleHeroSignal]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setRescueSceneIndex((prev) => (prev + 1) % rescueScenes.length);
    }, 3600);
    return () => window.clearInterval(intervalId);
  }, []);

  // Scroll-to-top progress state
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } =
        document.documentElement;
      const max = scrollHeight - clientHeight || 1;
      const pct = Math.min(100, Math.max(0, (scrollTop / max) * 100));
      setScrollProgress(pct);
      setShowScrollTop(scrollTop > 200);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Button and Card Title Character Stagger Animation
  useEffect(() => {
    const initCharacterStagger = () => {
      const offsetIncrement = 0.005;
      const elements = document.querySelectorAll(
        "[data-button-animate-chars], [data-title-animate-chars]",
      );

      elements.forEach((element) => {
        // Skip if already processed
        if (element.dataset.staggerProcessed === "1") return;

        const text = element.textContent;
        element.innerHTML = "";

        [...text].forEach((char, index) => {
          const span = document.createElement("span");
          span.textContent = char;
          span.style.transitionDelay = `${index * offsetIncrement}s`;

          if (char === " ") {
            span.style.whiteSpace = "pre";
          }

          element.appendChild(span);
        });

        // Mark as processed to prevent re-processing
        element.dataset.staggerProcessed = "1";
      });
    };

    initCharacterStagger();

    // Re-run on dynamic content changes (for cards that mount later)
    const observer = new MutationObserver(() => {
      initCharacterStagger();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  // Custom Node Component with Handles
  const CustomNode = ({ data }) => {
    const isMobile = window.innerWidth <= 768;

    return (
      <div
        style={{
          background: "rgba(255, 255, 255, 0.03)",
          border: `1px solid ${data.borderColor}`,
          borderRadius: isMobile ? "8px" : "12px",
          padding: isMobile ? "12px 16px" : "20px 24px",
          minWidth: isMobile ? "120px" : "180px",
          backdropFilter: "blur(8px)",
          position: "relative",
        }}
      >
        {/* Input handle (left side) */}
        <Handle
          type="target"
          position={Position.Left}
          style={{
            background: data.borderColor,
            width: isMobile ? "6px" : "8px",
            height: isMobile ? "6px" : "8px",
            border: isMobile
              ? "1px solid rgba(255, 255, 255, 0.3)"
              : "2px solid rgba(255, 255, 255, 0.3)",
          }}
        />

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}
        >
          <div
            style={{
              color: "#ffffff",
              fontSize: isMobile ? "14px" : "18px",
              fontWeight: "400",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              letterSpacing: "0.01em",
              textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
            }}
          >
            {data.label}
          </div>
          {data.subtitle && (
            <div
              style={{
                color: "rgba(255, 255, 255, 1)",
                fontSize: isMobile ? "12px" : "15px",
                fontWeight: "400",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                letterSpacing: "0.02em",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
              }}
            >
              {data.subtitle}
            </div>
          )}
        </div>

        {/* Output handle (right side) */}
        <Handle
          type="source"
          position={Position.Right}
          style={{
            background: data.borderColor,
            width: isMobile ? "6px" : "8px",
            height: isMobile ? "6px" : "8px",
            border: isMobile
              ? "1px solid rgba(255, 255, 255, 0.3)"
              : "2px solid rgba(255, 255, 255, 0.3)",
          }}
        />
      </div>
    );
  };

  const nodeTypes = {
    custom: CustomNode,
  };

  // React Flow: complex automation workflow configuration
  const initialNodes = [
    {
      id: "1",
      type: "custom",
      data: {
        label: "Document Upload",
        subtitle: "Multi-format support",
        borderColor: "rgba(147, 197, 253, 0.4)",
      },
      position: { x: 0, y: 120 },
    },
    {
      id: "2",
      type: "custom",
      data: {
        label: "Document Parser",
        subtitle: "OCR & Text extraction",
        borderColor: "rgba(196, 181, 253, 0.4)",
      },
      position: { x: 280, y: 120 },
    },
    {
      id: "3",
      type: "custom",
      data: {
        label: "NLP Analysis",
        subtitle: "Semantic understanding",
        borderColor: "rgba(251, 207, 232, 0.4)",
      },
      position: { x: 560, y: 0 },
    },
    {
      id: "4",
      type: "custom",
      data: {
        label: "Topic Extraction",
        subtitle: "Key concepts identified",
        borderColor: "rgba(165, 243, 252, 0.4)",
      },
      position: { x: 560, y: 120 },
    },
    {
      id: "5",
      type: "custom",
      data: {
        label: "Context Mapping",
        subtitle: "Relationship analysis",
        borderColor: "rgba(147, 197, 253, 0.4)",
      },
      position: { x: 560, y: 240 },
    },
    {
      id: "6",
      type: "custom",
      data: {
        label: "Reference Engine",
        subtitle: "Multi-source synthesis",
        borderColor: "rgba(196, 181, 253, 0.4)",
      },
      position: { x: 840, y: 60 },
    },
    {
      id: "7",
      type: "custom",
      data: {
        label: "Citation Generator",
        subtitle: "APA, MLA, Chicago",
        borderColor: "rgba(251, 207, 232, 0.4)",
      },
      position: { x: 840, y: 180 },
    },
    {
      id: "8",
      type: "custom",
      data: {
        label: "AI Assistant",
        subtitle: "Real-time Q&A",
        borderColor: "rgba(165, 243, 252, 0.4)",
      },
      position: { x: 1120, y: 120 },
    },
    {
      id: "9",
      type: "custom",
      data: {
        label: "Delivery",
        subtitle: "Export & finalize",
        borderColor: "rgba(147, 197, 253, 0.4)",
      },
      position: { x: 1400, y: 120 },
    },
  ];

  const initialEdges = [
    {
      id: "e1-2",
      source: "1",
      target: "2",
      animated: true,
      style: {
        stroke: "#D2691E",
        strokeWidth: 3,
        strokeOpacity: 1,
        strokeDasharray: "8,4",
      },
    },
    {
      id: "e2-3",
      source: "2",
      target: "3",
      animated: true,
      style: {
        stroke: "#CC5500",
        strokeWidth: 3,
        strokeOpacity: 1,
        strokeDasharray: "8,4",
      },
    },
    {
      id: "e2-4",
      source: "2",
      target: "4",
      animated: true,
      style: {
        stroke: "#E86A33",
        strokeWidth: 3,
        strokeOpacity: 1,
        strokeDasharray: "8,4",
      },
    },
    {
      id: "e2-5",
      source: "2",
      target: "5",
      animated: true,
      style: {
        stroke: "#DC5B29",
        strokeWidth: 3,
        strokeOpacity: 1,
        strokeDasharray: "8,4",
      },
    },
    {
      id: "e3-6",
      source: "3",
      target: "6",
      animated: true,
      style: {
        stroke: "#D2691E",
        strokeWidth: 3,
        strokeOpacity: 1,
        strokeDasharray: "8,4",
      },
    },
    {
      id: "e4-6",
      source: "4",
      target: "6",
      animated: true,
      style: {
        stroke: "#CC5500",
        strokeWidth: 3,
        strokeOpacity: 1,
        strokeDasharray: "8,4",
      },
    },
    {
      id: "e5-7",
      source: "5",
      target: "7",
      animated: true,
      style: {
        stroke: "#E86A33",
        strokeWidth: 3,
        strokeOpacity: 1,
        strokeDasharray: "8,4",
      },
    },
    {
      id: "e6-8",
      source: "6",
      target: "8",
      animated: true,
      style: {
        stroke: "#DC5B29",
        strokeWidth: 3,
        strokeOpacity: 1,
        strokeDasharray: "8,4",
      },
    },
    {
      id: "e7-8",
      source: "7",
      target: "8",
      animated: true,
      style: {
        stroke: "#D2691E",
        strokeWidth: 3,
        strokeOpacity: 1,
        strokeDasharray: "8,4",
      },
    },
    {
      id: "e8-9",
      source: "8",
      target: "9",
      animated: true,
      style: {
        stroke: "#CC5500",
        strokeWidth: 3,
        strokeOpacity: 1,
        strokeDasharray: "8,4",
      },
    },
  ];

  const [nodes, setNodes] = useState(initialNodes);
  const [edges, setEdges] = useState(initialEdges);

  // Debug: log edges to console
  useEffect(() => {
    console.log("React Flow Edges:", edges);
    console.log("React Flow Nodes:", nodes);
  }, [edges, nodes]);

  const onNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  );

  const onEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  );

  // Framer Motion: section load-ins — blur lift + snap easing; boxes use spring.
  const easeSnap = [0.22, 1, 0.36, 1];
  const viewport = { once: true, amount: 0.08 };

  const fadeUp = {
    hidden: { opacity: 0, y: 32, filter: "blur(8px)" },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: {
        duration: 0.52,
        ease: easeSnap,
        opacity: { duration: 0.42, ease: easeSnap },
        y: { duration: 0.52, ease: easeSnap },
        filter: { duration: 0.46, ease: easeSnap },
      },
    },
  };

  const fadeUpFast = {
    hidden: { opacity: 0, y: 18, filter: "blur(8px)" },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { duration: 0.3, ease: easeSnap },
    },
  };

  const clipReveal = {
    hidden: {
      clipPath: "inset(0% 0% 100% 0%)",
      opacity: 0,
      y: 32,
      filter: "blur(10px)",
    },
    visible: {
      clipPath: "inset(0% 0% 0% 0%)",
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: {
        duration: 0.6,
        ease: easeSnap,
        clipPath: { duration: 0.6, ease: easeSnap },
        filter: { duration: 0.44, ease: easeSnap },
        staggerChildren: 0.055,
        delayChildren: 0.06,
      },
    },
  };

  const wordReveal = {
    hidden: { opacity: 0, y: 24, rotateZ: -3 },
    visible: {
      opacity: 1,
      y: 0,
      rotateZ: 0,
      transition: {
        type: "spring",
        stiffness: 360,
        damping: 30,
        mass: 0.82,
      },
    },
  };

  const clipRevealBlock = {
    hidden: {
      clipPath: "inset(0% 3% 100% 3%)",
      opacity: 0,
      y: 36,
      scale: 0.97,
      filter: "blur(8px)",
    },
    visible: {
      clipPath: "inset(0% 0% 0% 0%)",
      opacity: 1,
      y: 0,
      scale: 1,
      filter: "blur(0px)",
      transition: {
        duration: 0.64,
        ease: easeSnap,
        clipPath: { duration: 0.64, ease: easeSnap },
        scale: { type: "spring", stiffness: 300, damping: 31 },
        filter: { duration: 0.5, ease: easeSnap },
        opacity: { duration: 0.42, ease: easeSnap },
      },
    },
  };

  /** Large panels (pricing, compare, vs columns): spring + depth */
  const boxReveal = {
    hidden: {
      opacity: 0,
      y: 52,
      scale: 0.93,
      filter: "blur(14px)",
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      filter: "blur(0px)",
      transition: {
        type: "spring",
        stiffness: 265,
        damping: 28,
        mass: 1.02,
        filter: { duration: 0.62, ease: easeSnap },
        opacity: { duration: 0.52, ease: easeSnap },
      },
    },
  };

  const staggerContainer = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.135, delayChildren: 0.075 },
    },
  };

  // Hero ref (parallax disabled for now)
  const heroRef = useRef(null);

  const scrollToDemo = () => {
    demoRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToFeatures = () => {
    featuresRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Only mount the ModernDemo while the section is on screen to avoid auto-scroll tug
  useEffect(() => {
    const node = demoRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      ([entry]) => setShowDemo(entry.isIntersecting),
      { threshold: 0.2 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      toast.error("Failed to sign out");
      console.error("Sign out error:", error);
    }
  };

  // Removed IntersectionObserver in favor of Framer Motion's whileInView
  useEffect(() => {
    // GSAP SVG-based backdrop animation (strokes, motion path, wiggles) + confetti burst
    if (!SHOW_GLOBAL_BACKDROP || !backdropRef.current) return;
    const q = gsap.utils.selector(backdropRef.current);

    // Disperse SVG groups so they aren't clustered in the center
    // Push key groups to distant quadrants of the viewBox (2058 x 871)
    // Note: staying within ~(-900..900, -350..350) to remain visible in viewBox
    gsap.set(q("#bang"), { x: -880, y: -300 }); // far upper-left
    gsap.set(q("#ffd"), { x: 820, y: 280 }); // far lower-right
    gsap.set(q("#wiggle"), { x: -760, y: 320 }); // far lower-left
    gsap.set(q("#spin"), { x: 860, y: -260 }); // far upper-right
    gsap.set(q("#plane"), { x: 680, y: -320 }); // upper-right further

    // Sprinkle elements: broader randomized spread within safe bounds
    const rand = gsap.utils.random;
    gsap.set(q(".sprinkle"), {
      x: () => rand(-920, 920, true),
      y: () => rand(-340, 340, true),
      opacity: () => rand(0.65, 1, true),
      scale: () => rand(0.8, 1.2, true),
    });

    // Confetti initial state
    gsap.set(q(".confetti img"), {
      x: () => gsap.utils.random(-200, 200, true),
      y: () => gsap.utils.random(-200, 200, true),
      scale: () => gsap.utils.random(0.5, 1),
      opacity: 0,
    });

    const tl = gsap.timeline({ defaults: { ease: "expo.out" } });

    // Prepare stroke-draw paths
    const strokePaths = [q("#path")[0], q("#path_2")[0]].filter(Boolean);
    tl.set(strokePaths, {
      strokeDasharray: (_i, el) =>
        typeof el.getTotalLength === "function" ? el.getTotalLength() : 0,
      strokeDashoffset: (_i, el) =>
        typeof el.getTotalLength === "function" ? el.getTotalLength() : 0,
    });

    // Pop in some hero groups
    tl.from(q(["#bang", "#ffd"]), {
      duration: 0.4,
      scale: 0.6,
      opacity: 0,
      transformOrigin: "50% 50%",
      stagger: 0.12,
      ease: "back.out(1.7)",
    });

    // Draw the paths
    if (strokePaths.length) {
      tl.to(
        strokePaths,
        {
          duration: 0.5,
          strokeDashoffset: 0,
          ease: "none",
          stagger: 0.06,
        },
        "<0.05",
      );
    }

    // Spin accent
    tl.fromTo(
      q("#spin"),
      { rotate: 0, transformOrigin: "50% 50%" },
      { rotate: 360, duration: 1.2, ease: "power2.inOut" },
      "<",
    );

    // Plane along motion path (align to #path if present)
    const pathForPlane = q("#path")[0] || q("#path_2")[0];
    if (pathForPlane) {
      tl.to(
        q("#plane"),
        {
          duration: 2,
          ease: "power1.inOut",
          motionPath: {
            path: pathForPlane,
            align: pathForPlane,
            autoRotate: true,
            alignOrigin: [0.5, 0.5],
          },
        },
        "<0.2",
      );
    }

    // Subtle wiggle
    tl.to(
      q("#wiggle"),
      {
        duration: 0.8,
        rotation: 6,
        yoyo: true,
        repeat: 3,
        transformOrigin: "50% 50%",
        ease: "sine.inOut",
      },
      "<",
    );

    // Confetti burst
    tl.to(
      q(".confetti img"),
      { opacity: 1, duration: 0.08, stagger: 0.015 },
      "<",
    )
      .to(
        q(".confetti img"),
        {
          duration: 1,
          x: () => gsap.utils.random(-800, 800, true),
          y: () => gsap.utils.random(-600, 600, true),
          rotation: () => gsap.utils.random(-360, 360, true),
          ease: "power2.out",
          stagger: 0.02,
        },
        "<",
      )
      .to(q(".confetti img"), { duration: 0.4, opacity: 0 }, ">-0.2");

    // No click-to-replay; runs once per load
    return () => {
      tl.kill();
    };
  }, []);

  // Osmo-style heading reveals (lines masked, staggered) applied to all page titles
  useEffect(() => {
    // Skip GSAP animations on mobile/tablet to prevent conflicts with Framer Motion
    if (window.innerWidth <= 1024) {
      return;
    }

    let splitInstances = [];
    let anims = [];
    let osmoEaseName = "power3.out";

    // Helper: custom line splitter (no SplitText dependency)
    const splitIntoLines = (el) => {
      if (!el) return null;
      if (!el.textContent) return null;

      const originalHTML = el.innerHTML;
      const originalClass = el.className;
      // Force non-flex layout to avoid gaps from flex/grid on headings
      el.classList.add("split-active");

      // If this element is a container with block-level children, don't split it – children will be processed separately
      if (el.querySelector("h1,h2,h3,h4,br,p,div,ul,ol,li")) {
        // remove temp class and bail
        try {
          el.className = originalClass;
        } catch (_) {}
        return null;
      }

      // Walk only text nodes so we preserve existing markup/spans
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
          if (!node.nodeValue) return NodeFilter.FILTER_REJECT;
          return node.nodeValue.trim().length
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        },
      });

      const textNodes = [];
      while (walker.nextNode()) textNodes.push(walker.currentNode);

      textNodes.forEach((textNode) => {
        const frag = document.createDocumentFragment();
        const parts = textNode.nodeValue.split(/(\s+)/); // keep spaces
        parts.forEach((token) => {
          if (!token) return;
          if (/^\s+$/.test(token)) {
            frag.appendChild(document.createTextNode(token));
          } else {
            const wordSpan = document.createElement("span");
            wordSpan.className = "word";
            // Create letters
            for (let i = 0; i < token.length; i += 1) {
              const ch = token[i];
              const letterSpan = document.createElement("span");
              letterSpan.className = "letter";
              letterSpan.textContent = ch;
              wordSpan.appendChild(letterSpan);
            }
            frag.appendChild(wordSpan);
          }
        });
        textNode.parentNode.replaceChild(frag, textNode);
      });

      const wordEls = Array.from(el.querySelectorAll(".word"));
      if (!wordEls.length) {
        el.innerHTML = originalHTML;
        return null;
      }

      // Force reflow so positions are computed
      // eslint-disable-next-line no-unused-expressions
      el.offsetHeight;

      // Group words by line via offsetTop
      const lines = [];
      let currentLineTop = null;
      let current = [];
      wordEls.forEach((w) => {
        const top = w.offsetTop;
        if (currentLineTop === null) {
          currentLineTop = top;
          current.push(w);
          return;
        }
        if (Math.abs(top - currentLineTop) <= 2) {
          current.push(w);
        } else {
          lines.push(current);
          current = [w];
          currentLineTop = top;
        }
      });
      if (current.length) lines.push(current);

      // Wrap each line and insert spacing between words so text doesn't concatenate
      lines.forEach((lineWords) => {
        const wrapper = document.createElement("span");
        wrapper.className = "line";
        wrapper.style.display = "block";
        wrapper.style.overflow = "hidden";
        const first = lineWords[0];
        first.parentNode.insertBefore(wrapper, first);
        lineWords.forEach((w, idx) => {
          wrapper.appendChild(w);
          if (idx < lineWords.length - 1) {
            wrapper.appendChild(document.createTextNode(" "));
          }
        });
      });

      return {
        revert: () => {
          el.innerHTML = originalHTML;
          // remove temp class if still present
          try {
            el.className = originalClass;
          } catch (_) {}
        },
      };
    };

    // Optionally load CustomEase
    const load = async () => {
      try {
        const customEaseMod = await import("gsap/CustomEase").catch(() => null);
        if (
          customEaseMod &&
          (customEaseMod.default || customEaseMod.CustomEase)
        ) {
          try {
            const CustomEase =
              customEaseMod.default || customEaseMod.CustomEase;
            gsap.registerPlugin(CustomEase);
            CustomEase.create("osmo-ease", "0.625, 0.05, 0, 1");
            osmoEaseName = "osmo-ease";
          } catch (_) {
            // ignore
          }
        }

        const selectors = [
          `.${styles.title}`,
          `.${styles.title1}`,
          `.${styles.sectionTitle}`,
          `.${styles.valueTitle}`,
          `.${styles.faqQuestion}`,
          `.${styles.heroTitle}`,
          `.${styles.statValue}`,
          `.${styles.statLabel}`,
          `.${styles.tierName}`,
          // fallback generic headings in case some titles don't use module classes
          "h1",
          "h2",
          "h3",
          "h4",
          // role headings and common title/subtitle class patterns
          '[role="heading"]',
          '[class*="title"]',
          '[class*="Title"]',
          '[class*="subtitle"]',
          '[class*="Subtitle"]',
        ].join(",");

        // select and then exclude anything inside the modern demo section
        const heads = gsap.utils
          .toArray(selectors)
          .filter(Boolean)
          .filter((el) => !(el && el.closest(`.${styles.demoSection}`)));
        try {
          console.debug("[Split] heads selected:", heads.length);
        } catch (_) {}

        // Use ScrollTrigger.batch so elements that animate into DOM later are handled
        const config = {
          lines: { duration: 0.5, stagger: 0.05 },
          words: { duration: 0.4, stagger: 0.04 },
          letters: { duration: 0.25, stagger: 0.005 },
        };

        // round-robin index removed; we animate by words only

        ScrollTrigger.batch(heads, {
          onEnter: (elements) => {
            elements.forEach((el) => {
              if (!el || el.dataset.splitDone === "1") return;
              // Skip elements with character stagger animation
              if (el.querySelector("[data-title-animate-chars]")) return;

              // Split and animate
              const st = splitIntoLines(el);
              if (st) splitInstances.push(st);

              // sanitize any previous transforms to avoid invisible text
              try {
                gsap.set(el.querySelectorAll(".letter,.word,.line"), {
                  yPercent: 0,
                  clearProps: "transform",
                });
              } catch (_) {}
              // Always animate by words
              const words = el.querySelectorAll(".word");

              let targets = null;
              let timing = config.words;
              if (words && words.length) {
                targets = words;
                timing = config.words;
              }

              if (targets && targets.length) {
                const a = gsap.fromTo(
                  targets,
                  { yPercent: 125 },
                  {
                    yPercent: 0,
                    duration: timing.duration,
                    stagger: timing.stagger,
                    ease: osmoEaseName,
                  },
                );
                anims.push(a);
              } else {
                const a = gsap.fromTo(
                  el,
                  { y: 24, opacity: 0 },
                  { y: 0, opacity: 1, duration: 0.4, ease: osmoEaseName },
                );
                anims.push(a);
              }
              el.dataset.splitDone = "1";
            });
          },
          start: "top 85%",
          once: true,
        });

        // Explicit per-element triggers for non-hero titles to ensure word-reveal (not just fade)
        const ensureWordRevealSelectors = [
          `.${styles.sectionTitle}`,
          `.${styles.sectionSubtitle}`,
          `.${styles.valueTitle}`,
          `.${styles.faqQuestion}`,
          `.${styles.tierName}`,
          `.${styles.statLabel}`,
          `.${styles.statValue}`,
        ].join(",");
        const ensureEls = gsap.utils
          .toArray(ensureWordRevealSelectors)
          .filter(Boolean)
          .filter((el) => !(el && el.closest(`.${styles.demoSection}`)));
        try {
          const sectionTitles = ensureEls.filter(
            (el) => el.classList && el.classList.contains(styles.sectionTitle),
          ).length;
          console.debug(
            "[Split] ensureEls total:",
            ensureEls.length,
            "sectionTitles:",
            sectionTitles,
          );
        } catch (_) {}

        ensureEls.forEach((el) => {
          ScrollTrigger.create({
            trigger: el,
            start: "top 90%",
            once: true,
            onEnter: () => {
              if (!el) return;
              try {
                console.debug(
                  "[Split] onEnter ensure:",
                  el.id || el.className,
                  "splitDone=",
                  el.dataset.splitDone,
                );
              } catch (_) {}
              // If previously flagged done but lacks .word wrappers, allow processing
              if (el.dataset.splitDone === "1" && el.querySelector(".word"))
                return;
              const st = splitIntoLines(el);
              if (st) splitInstances.push(st);
              try {
                gsap.set(el.querySelectorAll(".letter,.word,.line"), {
                  yPercent: 0,
                  clearProps: "transform",
                });
              } catch (_) {}
              const words = el.querySelectorAll(".word");
              try {
                console.debug(
                  "[Split] ensure words count:",
                  words ? words.length : 0,
                );
              } catch (_) {}
              if (words && words.length) {
                gsap.fromTo(
                  words,
                  { yPercent: 125 },
                  {
                    yPercent: 0,
                    duration: 0.4,
                    stagger: 0.04,
                    ease: osmoEaseName,
                  },
                );
              }
              el.dataset.splitDone = "1";
            },
          });
        });

        // Guarantee the exact same hero-title word-reveal for the Features title via a dedicated trigger
        const featuresTitle = document.getElementById("featuresTitle");
        if (featuresTitle) {
          try {
            console.debug(
              "[Split] featuresTitle found:",
              !!featuresTitle,
              "text=",
              featuresTitle.textContent,
            );
          } catch (_) {}
          ScrollTrigger.create({
            trigger: featuresTitle,
            start: "top 90%",
            once: true,
            // markers removed per request
            onEnter: () => {
              const target =
                featuresTitle.querySelector(".splitTarget") || featuresTitle;
              try {
                console.debug(
                  "[Split] features onEnter splitDone=",
                  target.dataset.splitDone,
                  "target=",
                  target.className || target.tagName,
                );
              } catch (_) {}
              if (
                target.dataset.splitDone === "1" &&
                target.querySelector(".word")
              ) {
                try {
                  console.debug("[Split] features already split; skipping");
                } catch (_) {}
                return;
              }
              const st = splitIntoLines(target);
              if (st) splitInstances.push(st);
              try {
                gsap.set(target.querySelectorAll(".letter,.word,.line"), {
                  yPercent: 0,
                  clearProps: "transform",
                });
              } catch (_) {}
              const words = target.querySelectorAll(".word");
              try {
                console.debug(
                  "[Split] features words count:",
                  words ? words.length : 0,
                );
              } catch (_) {}
              if (words && words.length) {
                gsap.fromTo(
                  words,
                  { yPercent: 125 },
                  {
                    yPercent: 0,
                    duration: 0.4,
                    stagger: 0.04,
                    ease: osmoEaseName,
                  },
                );
              }
              target.dataset.splitDone = "1";
              featuresTitle.dataset.splitDone = "1";
            },
          });

          // If already in view on load, run immediately
          try {
            const rect = featuresTitle.getBoundingClientRect();
            const inView =
              rect.top < window.innerHeight * 0.9 && rect.bottom > 0;
            console.debug("[Split] features immediate check inView=", inView);
            const target =
              featuresTitle.querySelector(".splitTarget") || featuresTitle;
            if (
              inView &&
              !(
                target.dataset.splitDone === "1" &&
                target.querySelector(".word")
              )
            ) {
              const st = splitIntoLines(target);
              if (st) splitInstances.push(st);
              try {
                gsap.set(target.querySelectorAll(".letter,.word,.line"), {
                  yPercent: 0,
                  clearProps: "transform",
                });
              } catch (_) {}
              const wordsNow = target.querySelectorAll(".word");
              console.debug(
                "[Split] features immediate words count:",
                wordsNow ? wordsNow.length : 0,
              );
              if (wordsNow && wordsNow.length) {
                gsap.fromTo(
                  wordsNow,
                  { yPercent: 125 },
                  {
                    yPercent: 0,
                    duration: 0.4,
                    stagger: 0.04,
                    ease: osmoEaseName,
                  },
                );
              }
              target.dataset.splitDone = "1";
              featuresTitle.dataset.splitDone = "1";
            }
          } catch (_) {}
        }

        // Card load-in animations disabled per request

        // Also process any headings already in view on load (hero, above-the-fold)
        heads.forEach((el) => {
          if (el.dataset.splitDone === "1") return;
          const rect = el.getBoundingClientRect();
          const inView =
            rect.top < window.innerHeight * 0.85 && rect.bottom > 0;
          if (!inView) return;

          const st = splitIntoLines(el);
          if (st) splitInstances.push(st);
          try {
            gsap.set(el.querySelectorAll(".letter,.word,.line"), {
              yPercent: 0,
              clearProps: "transform",
            });
          } catch (_) {}

          // Always animate by words; if none, animate the element itself (match batch behavior)
          const words = el.querySelectorAll(".word");
          const config = {
            lines: { duration: 0.5, stagger: 0.05 },
            words: { duration: 0.4, stagger: 0.04 },
            letters: { duration: 0.25, stagger: 0.005 },
          };
          if (words.length) {
            const a = gsap.fromTo(
              words,
              { yPercent: 125 },
              {
                yPercent: 0,
                duration: config.words.duration,
                stagger: config.words.stagger,
                ease: osmoEaseName,
              },
            );
            anims.push(a);
          } else {
            const a = gsap.fromTo(
              el,
              { y: 24, opacity: 0 },
              { y: 0, opacity: 1, duration: 0.4, ease: osmoEaseName },
            );
            anims.push(a);
          }
          el.dataset.splitDone = "1";
        });

        // Ensure layout is measured correctly and triggers compute with final fonts
        try {
          ScrollTrigger.refresh();
        } catch (_) {
          /* noop */
        }
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => {
            try {
              ScrollTrigger.refresh();
            } catch (_) {
              /* noop */
            }
          });
        }

        // MutationObserver: process headings that mount later
        const selectorString = selectors;
        const processHeading = (el) => {
          if (!el || el.dataset.splitDone === "1") return;
          // avoid re-splitting if already contains .line/.word
          if (el.querySelector(".line, .word, .letter")) return;
          const st = splitIntoLines(el);
          if (st) splitInstances.push(st);
          try {
            gsap.set(el.querySelectorAll(".letter,.word,.line"), {
              yPercent: 0,
              clearProps: "transform",
            });
          } catch (_) {}
          // Always animate by words
          const words = el.querySelectorAll(".word");
          const config = {
            lines: { duration: 0.5, stagger: 0.05 },
            words: { duration: 0.4, stagger: 0.04 },
            letters: { duration: 0.25, stagger: 0.005 },
          };
          let targets = null;
          let timing = config.words;
          if (words.length) {
            targets = words;
            timing = config.words;
          }

          if (targets) {
            const a = gsap.fromTo(
              targets,
              { yPercent: 125 },
              {
                yPercent: 0,
                duration: timing.duration,
                stagger: timing.stagger,
                ease: osmoEaseName,
              },
            );
            anims.push(a);
          }
          el.dataset.splitDone = "1";
        };

        const mo = new MutationObserver((mutations) => {
          const added = [];
          mutations.forEach((m) => {
            m.addedNodes &&
              m.addedNodes.forEach((node) => {
                if (!(node instanceof HTMLElement)) return;
                if (node.matches && node.matches(selectorString))
                  added.push(node);
                added.push(
                  ...Array.from(
                    node.querySelectorAll
                      ? node.querySelectorAll(selectorString)
                      : [],
                  ),
                );
              });
          });
          // De-dupe
          const seen = new Set();
          added.forEach((el) => {
            if (!el || seen.has(el)) return;
            seen.add(el);
            processHeading(el);
          });
        });
        mo.observe(document.documentElement, {
          childList: true,
          subtree: true,
        });

        // store observer to disconnect later
        (window.__landingMO__ ||= []).push(mo);
      } catch (_) {
        // ignore errors and leave page functional
      }
    };

    load();

    return () => {
      anims.forEach((a) => a && a.kill && a.kill());
      splitInstances.forEach((st) => st && st.revert && st.revert());
      if (window.__landingMO__) {
        window.__landingMO__.forEach((mo) => {
          try {
            mo.disconnect();
          } catch (_) {}
        });
        window.__landingMO__ = [];
      }
    };
  }, []);

  // Section decorative objects: drop-in on scroll
  useEffect(() => {
    // Animate any element with the decorObject class when it enters viewport
    const els = gsap.utils.toArray(`.${styles.decorObject}`);
    const anims = els.map((el) =>
      gsap.fromTo(
        el,
        {
          opacity: 0,
          y: 24,
          scale: 0.95,
          rotate: gsap.utils.random(-6, 6),
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          rotate: 0,
          duration: 0.5,
          ease: "expo.out",
          scrollTrigger: {
            trigger: el,
            start: "top 80%",
            toggleActions: "play none none none",
          },
        },
      ),
    );
    return () => {
      anims.forEach((a) => a.kill());
    };
  }, []);

  // GSAP Parallax scroll effects for backgrounds only - Framer Motion handles content animations
  useEffect(() => {
    const parallaxAnimations = [];

    // Disable parallax on mobile for better performance
    const isMobile = window.innerWidth <= 768;

    if (isMobile) {
      return; // Skip parallax on mobile
    }

    // Wait a bit for DOM to be ready
    const timer = setTimeout(() => {
      // Refresh ScrollTrigger to get accurate measurements
      ScrollTrigger.refresh();

      const sections = document.querySelectorAll(
        `.${styles.section}, .${styles.header}`,
      );

      sections.forEach((section) => {
        // Create a subtle parallax effect by moving the background position
        const animation = gsap.to(section, {
          backgroundPosition: "50% 50%",
          ease: "none",
          scrollTrigger: {
            trigger: section,
            start: "top bottom",
            end: "bottom top",
            scrub: 1.5,
          },
        });
        parallaxAnimations.push(animation);
      });

      // Force an immediate refresh after setup
      ScrollTrigger.refresh();
    }, 300);

    return () => {
      clearTimeout(timer);
      parallaxAnimations.forEach((anim) => anim.kill());
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <div
      className={`${styles.aboutContainer} ${styles.motionControlled}${
        navLiquidBackdropUnsupported
          ? ` ${styles.aboutContainerNavBlurFallback}`
          : ""
      }`}
    >
      <LiquidBackground className={styles.liquidBgLayer} />
      <div className={styles.interstellarFx} aria-hidden="true">
        <div className={styles.interstellarStars} />
        <div className={styles.interstellarNebula} />
        <div className={styles.interstellarHorizon} />
      </div>
      {/* GSAP Backdrop: behind all content (objects only) */}
      {SHOW_GLOBAL_BACKDROP && (
        <div
          ref={backdropRef}
          className={styles.gsapBackdrop}
          aria-hidden="true"
        >
          <div
            className={styles.explodeContainer}
            role="img"
            aria-label="a big colourful confetti explosion"
          >
            <svg
              className={styles.explodeSvg}
              id="explode"
              viewBox="0 0 2058 871"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              xmlnsXlink="http://www.w3.org/1999/xlink"
            >
              <g id="GSAP_GTM_blog_hero_2400x1260">
                <g id="bang">
                  <g id="Vector">
                    <path
                      d="M833.956 626.101L747.149 801.943C745.505 805.265 749.467 808.518 752.373 806.239L932.942 664.473C934.689 663.099 937.25 663.708 938.201 665.725L981.674 758.526C983.006 761.361 987.106 761.117 988.092 758.143L1032.59 623.926C1033.4 621.474 1036.49 620.743 1038.31 622.569L1166.26 751.22C1169.02 754.003 1173.56 750.733 1171.79 747.202L1087.08 576.839C1085.69 574.073 1088.34 570.995 1091.26 571.986L1216.42 613.942C1219.88 615.107 1222.55 610.776 1219.97 608.184L1087.3 474.785C1085.49 472.959 1086.21 469.845 1088.65 469.027L1210.09 428.324C1213.81 427.072 1212.93 421.54 1209 421.54H1089.81C1087.04 421.54 1085.4 418.444 1086.94 416.13L1174.11 284.645C1176.08 281.67 1172.86 278 1169.68 279.6L988.922 370.47C987.036 371.409 984.753 370.47 984.096 368.452L939.654 234.374C938.599 231.209 934.136 231.209 933.098 234.374L889.158 366.921C888.345 369.374 885.248 370.104 883.432 368.278L798.528 282.905C796.019 280.383 791.833 282.818 792.732 286.262L825.998 411.382C826.777 414.304 823.75 416.757 821.068 415.382L662.071 333.176C658.56 331.367 655.256 335.924 658.041 338.725L785.432 466.818C787.248 468.645 786.522 471.758 784.083 472.576L662.642 513.279C658.923 514.532 659.805 520.063 663.732 520.063H787.629C790.431 520.063 792.075 523.229 790.466 525.542L697.414 659.22C695.493 661.968 698.175 665.586 701.34 664.508L829.752 621.265C832.676 620.273 835.323 623.335 833.956 626.118V626.101Z"
                      fill="url(#paint0_linear_6028_4729)"
                    />
                    <path
                      d="M833.956 626.101L747.149 801.943C745.505 805.265 749.467 808.518 752.373 806.239L932.942 664.473C934.689 663.099 937.25 663.708 938.201 665.725L981.674 758.526C983.006 761.361 987.106 761.117 988.092 758.143L1032.59 623.926C1033.4 621.474 1036.49 620.743 1038.31 622.569L1166.26 751.22C1169.02 754.003 1173.56 750.733 1171.79 747.202L1087.08 576.839C1085.69 574.073 1088.34 570.995 1091.26 571.986L1216.42 613.942C1219.88 615.107 1222.55 610.776 1219.97 608.184L1087.3 474.785C1085.49 472.959 1086.21 469.845 1088.65 469.027L1210.09 428.324C1213.81 427.072 1212.93 421.54 1209 421.54H1089.81C1087.04 421.54 1085.4 418.444 1086.94 416.13L1174.11 284.645C1176.08 281.67 1172.86 278 1169.68 279.6L988.922 370.47C987.036 371.409 984.753 370.47 984.096 368.452L939.654 234.374C938.599 231.209 934.136 231.209 933.098 234.374L889.158 366.921C888.345 369.374 885.248 370.104 883.432 368.278L798.528 282.905C796.019 280.383 791.833 282.818 792.732 286.262L825.998 411.382C826.777 414.304 823.75 416.757 821.068 415.382L662.071 333.176C658.56 331.367 655.256 335.924 658.041 338.725L785.432 466.818C787.248 468.645 786.522 471.758 784.083 472.576L662.642 513.279C658.923 514.532 659.805 520.063 663.732 520.063H787.629C790.431 520.063 792.075 523.229 790.466 525.542L697.414 659.22C695.493 661.968 698.175 665.586 701.34 664.508L829.752 621.265C832.676 620.273 835.323 623.335 833.956 626.118V626.101Z"
                      fill="url(#pattern)"
                      fillOpacity="0.6"
                    />
                  </g>
                </g>
                <g id="ffd">
                  <path
                    d="M906.652 763.429C906.652 760.441 910.072 758.744 912.451 760.552L947.419 787.123C949.322 788.569 949.322 791.431 947.419 792.877L912.451 819.447C910.072 821.255 906.653 819.559 906.652 816.571V797.522L877.799 819.447C875.42 821.255 872 819.559 872 816.571V763.429C872 760.441 875.42 758.744 877.799 760.552L906.652 782.476V763.429Z"
                    fill="#05F34A"
                  />
                  <path
                    fill="url(#pattern)"
                    fillOpacity="0.6"
                    style={{ mixBlendMode: "multiply" }}
                    d="M906.652 763.429C906.652 760.441 910.072 758.744 912.451 760.552L947.419 787.123C949.322 788.569 949.322 791.431 947.419 792.877L912.451 819.447C910.072 821.255 906.653 819.559 906.652 816.571V797.522L877.799 819.447C875.42 821.255 872 819.559 872 816.571V763.429C872 760.441 875.42 758.744 877.799 760.552L906.652 782.476V763.429Z"
                  />
                </g>
                <g id="wiggle">
                  <g>
                    <path
                      d="M890.757 732.409C873.509 732.409 860.512 731.11 849.85 728.297C819.605 720.346 810.296 700.231 807.443 688.947C803.899 674.95 804.562 653.594 827.861 631.243C835.873 623.566 846.55 615.514 861.463 605.933C885.771 590.32 917.861 572.903 951.823 554.448C971.001 544.03 991.981 532.645 1011.78 521.346C1004.27 522.226 996.592 523.15 988.797 524.088C935.252 530.509 874.576 537.782 822.66 537.782C798.308 537.782 779.49 535.891 765.124 532.01C734.288 523.669 722.689 505.488 718.41 491.708C714.894 480.438 712.17 457.913 733.381 433.671C740.254 425.807 749.36 417.986 761.219 409.79C762.631 408.809 765.095 407.15 768.438 404.899C773.928 401.19 782.069 395.693 791.824 388.983L760.556 352.649L793.899 323.847C795.037 322.866 821.997 299.649 853.855 278.74C873.826 265.638 891.91 255.97 907.616 250.011C936.592 239.001 961.347 239.347 981.189 251.006C996.866 260.227 1006.56 276.489 1007.13 294.497C1008.09 325.319 984.892 354.496 918.509 406.01C899.1 421.074 878.812 435.764 861.017 448.274C898.956 445.994 940.497 441.002 978.307 436.471C1016.04 431.94 1051.68 427.669 1079.4 426.558C1096.55 425.879 1109.59 426.399 1120.44 428.231C1149.42 433.094 1162.38 448.318 1168.16 460.236C1175.44 475.243 1175.11 492.415 1167.24 508.576C1163.1 517.061 1156.75 525.545 1147.83 534.477C1120.03 562.312 1065.06 593.235 1009.37 623.566C1051.52 609.324 1078.59 592.86 1079.07 592.571L1078.97 592.643L1125.61 667.519C1121.37 670.174 1020.25 732.395 890.771 732.395L890.757 732.409Z"
                      fill="url(#paint1_linear_6028_4729)"
                    />
                    <path
                      d="M890.757 732.409C873.509 732.409 860.512 731.11 849.85 728.297C819.605 720.346 810.296 700.231 807.443 688.947C803.899 674.95 804.562 653.594 827.861 631.243C835.873 623.566 846.55 615.514 861.463 605.933C885.771 590.32 917.861 572.903 951.823 554.448C971.001 544.03 991.981 532.645 1011.78 521.346C1004.27 522.226 996.592 523.15 988.797 524.088C935.252 530.509 874.576 537.782 822.66 537.782C798.308 537.782 779.49 535.891 765.124 532.01C734.288 523.669 722.689 505.488 718.41 491.708C714.894 480.438 712.17 457.913 733.381 433.671C740.254 425.807 749.36 417.986 761.219 409.79C762.631 408.809 765.095 407.15 768.438 404.899C773.928 401.19 782.069 395.693 791.824 388.983L760.556 352.649L793.899 323.847C795.037 322.866 821.997 299.649 853.855 278.74C873.826 265.638 891.91 255.97 907.616 250.011C936.592 239.001 961.347 239.347 981.189 251.006C996.866 260.227 1006.56 276.489 1007.13 294.497C1008.09 325.319 984.892 354.496 918.509 406.01C899.1 421.074 878.812 435.764 861.017 448.274C898.956 445.994 940.497 441.002 978.307 436.471C1016.04 431.94 1051.68 427.669 1079.4 426.558C1096.55 425.879 1109.59 426.399 1120.44 428.231C1149.42 433.094 1162.38 448.318 1168.16 460.236C1175.44 475.243 1175.11 492.415 1167.24 508.576C1163.1 517.061 1156.75 525.545 1147.83 534.477C1120.03 562.312 1065.06 593.235 1009.37 623.566C1051.52 609.324 1078.59 592.86 1079.07 592.571L1078.97 592.643L1125.61 667.519C1121.37 670.174 1020.25 732.395 890.771 732.395L890.757 732.409Z"
                      fill="url(#pattern1_6028_4729)"
                      fillOpacity="0.6"
                    />
                  </g>
                  <g fill="url(#pattern)" style={{ mixBlendMode: "multiply" }}>
                    <path d="M890.757 732.409C873.509 732.409 860.512 731.11 849.85 728.297C819.605 720.346 810.296 700.231 807.443 688.947C803.899 674.95 804.562 653.594 827.861 631.243C835.873 623.566 846.55 615.514 861.463 605.933C885.771 590.32 917.861 572.903 951.823 554.448C971.001 544.03 991.981 532.645 1011.78 521.346C1004.27 522.226 996.592 523.15 988.797 524.088C935.252 530.509 874.576 537.782 822.66 537.782C798.308 537.782 779.49 535.891 765.124 532.01C734.288 523.669 722.689 505.488 718.41 491.708C714.894 480.438 712.17 457.913 733.381 433.671C740.254 425.807 749.36 417.986 761.219 409.79C762.631 408.809 765.095 407.15 768.438 404.899C773.928 401.19 782.069 395.693 791.824 388.983L760.556 352.649L793.899 323.847C795.037 322.866 821.997 299.649 853.855 278.74C873.826 265.638 891.91 255.97 907.616 250.011C936.592 239.001 961.347 239.347 981.189 251.006C996.866 260.227 1006.56 276.489 1007.13 294.497C1008.09 325.319 984.892 354.496 918.509 406.01C899.1 421.074 878.812 435.764 861.017 448.274C898.956 445.994 940.497 441.002 978.307 436.471C1016.04 431.94 1051.68 427.669 1079.4 426.558C1096.55 425.879 1109.59 426.399 1120.44 428.231C1149.42 433.094 1162.38 448.318 1168.16 460.236C1175.44 475.243 1175.11 492.415 1167.24 508.576C1163.1 517.061 1156.75 525.545 1147.83 534.477C1120.03 562.312 1065.06 593.235 1009.37 623.566C1051.52 609.324 1078.59 592.86 1079.07 592.571L1078.97 592.643L1125.61 667.519C1121.37 670.174 1020.25 732.395 890.771 732.395L890.757 732.409Z" />
                    <path d="M890.757 732.409C873.509 732.409 860.512 731.11 849.85 728.297C819.605 720.346 810.296 700.231 807.443 688.947C803.899 674.95 804.562 653.594 827.861 631.243C835.873 623.566 846.55 615.514 861.463 605.933C885.771 590.32 917.861 572.903 951.823 554.448C971.001 544.03 991.981 532.645 1011.78 521.346C1004.27 522.226 996.592 523.15 988.797 524.088C935.252 530.509 874.576 537.782 822.66 537.782C798.308 537.782 779.49 535.891 765.124 532.01C734.288 523.669 722.689 505.488 718.41 491.708C714.894 480.438 712.17 457.913 733.381 433.671C740.254 425.807 749.36 417.986 761.219 409.79C762.631 408.809 765.095 407.15 768.438 404.899C773.928 401.19 782.069 395.693 791.824 388.983L760.556 352.649L793.899 323.847C795.037 322.866 821.997 299.649 853.855 278.74C873.826 265.638 891.91 255.97 907.616 250.011C936.592 239.001 961.347 239.347 981.189 251.006C996.866 260.227 1006.56 276.489 1007.13 294.497C1008.09 325.319 984.892 354.496 918.509 406.01C899.1 421.074 878.812 435.764 861.017 448.274C898.956 445.994 940.497 441.002 978.307 436.471C1016.04 431.94 1051.68 427.669 1079.4 426.558C1096.55 425.879 1109.59 426.399 1120.44 428.231C1149.42 433.094 1162.38 448.318 1168.16 460.236C1175.44 475.243 1175.11 492.415 1167.24 508.576C1163.1 517.061 1156.75 525.545 1147.83 534.477C1120.03 562.312 1065.06 593.235 1009.37 623.566C1051.52 609.324 1078.59 592.86 1079.07 592.571L1078.97 592.643L1125.61 667.519C1121.37 670.174 1020.25 732.395 890.771 732.395L890.757 732.409Z" />
                  </g>
                </g>
                <rect
                  id="box"
                  x="539"
                  y="469.42"
                  width="87.0018"
                  height="80.7347"
                  transform="rotate(-83.3705 539 469.42)"
                  fill="url(#pattern)"
                />
                <path
                  className="sprinkle"
                  d="M733.022 222.569L709.927 203.163C709.819 203.073 709.659 203.087 709.569 203.194L690.163 226.289C690.073 226.397 690.087 226.556 690.194 226.647L713.29 246.052C713.397 246.143 713.557 246.129 713.647 246.021L733.053 222.926C733.143 222.819 733.129 222.659 733.022 222.569Z"
                  fill="#397DFF"
                />
                <path
                  id="path"
                  d="M538.5 556.481C565.165 497.803 621.326 446.333 685.817 449.897C742.695 453.011 791.23 499.923 807.851 554.295C822.28 601.485 806.483 665.329 758.267 675.637C710.051 685.944 669.808 635.984 657.212 588.396C638.16 517.04 652.243 437.987 694.679 377.68C737.115 317.374 806.881 277.006 880.5 270.501"
                  stroke="#FEC5FB"
                  strokeWidth="12"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                />
                <g id="spin">
                  <path
                    d="M1122.34 375.405C1122.45 375.008 1122.25 374.601 1121.87 374.451C1101.02 366.065 1089.13 344.311 1094.88 323.286C1100.64 302.26 1122.05 289.271 1144.38 292.258C1144.78 292.312 1145.17 292.06 1145.28 291.663L1153.28 262.468C1153.4 262.013 1153.12 261.547 1152.66 261.462C1112.31 253.932 1072.68 276.799 1062.29 314.709C1051.9 352.62 1074.53 391.867 1113.29 405.168C1113.74 405.32 1114.22 405.057 1114.35 404.602L1122.35 375.408L1122.34 375.405Z"
                    fill="url(#paint2_linear_6028_4729)"
                  />
                  <path
                    d="M1122.34 375.405C1122.45 375.008 1122.25 374.601 1121.87 374.451C1101.02 366.065 1089.13 344.311 1094.88 323.286C1100.64 302.26 1122.05 289.271 1144.38 292.258C1144.78 292.312 1145.17 292.06 1145.28 291.663L1153.28 262.468C1153.4 262.013 1153.12 261.547 1152.66 261.462C1112.31 253.932 1072.68 276.799 1062.29 314.709C1051.9 352.62 1074.53 391.867 1113.29 405.168C1113.74 405.32 1114.22 405.057 1114.35 404.602L1122.35 375.408L1122.34 375.405Z"
                    fill="url(#pattern)"
                    fillOpacity="0.6"
                  />
                </g>
                <path
                  className="sprinkle"
                  d="M1172.43 119.19C1161.04 107.84 1141.32 122.2 1150.1 137.037"
                  stroke="#FF783E"
                  strokeWidth="12"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  className="sprinkle"
                  d="M1178.57 43.2882C1173.21 45.868 1172.18 51.6629 1174.14 56.9067C1177.71 66.5265 1181.31 76.1267 1184.88 85.7464C1186.82 90.9677 1194.09 92.8281 1198.6 90.646C1203.96 88.0661 1204.99 82.2714 1203.03 77.0275C1199.46 67.4078 1195.86 57.8074 1192.29 48.1877C1190.35 42.9664 1183.08 41.106 1178.57 43.2882Z"
                  fill="#BAA5F5"
                  stroke="black"
                  strokeWidth="3"
                  strokeMiterlimit="10"
                />
                <circle
                  className="sprinkle"
                  cx="783.75"
                  cy="239.751"
                  r="17.25"
                  fill="#FAF005"
                />
                <circle
                  className="sprinkle"
                  cx="1207.5"
                  cy="336.501"
                  r="28.5"
                  fill="#FAF005"
                />
                <g id="plane">
                  <g className="innerplane">
                    <g id="Group">
                      <path
                        id="Vector_10"
                        d="M1286.78 195.303L1452.45 134.639L1293.52 213.708L1286.78 195.303Z"
                        fill="#05F34A"
                      />
                      <path
                        id="Vector_11"
                        d="M1446.47 137.211L1293.72 213.209L1287.24 195.519L1446.47 137.211ZM1458.42 132.07L1286.32 195.088L1293.32 214.21L1458.42 132.07Z"
                        fill="#F5F9EA"
                      />
                    </g>
                    <path
                      id="Vector_12"
                      d="M1458.41 132.072L1228.95 216.095L1253.19 163.85L1292.99 154.093L1458.41 132.072Z"
                      fill="#05F34A"
                      stroke="#1B1E1A"
                      strokeWidth="3"
                      strokeLinecap="square"
                      strokeLinejoin="round"
                    />
                    <path
                      id="Vector_13"
                      d="M1265.31 137.726L1458.42 132.071L1273.13 159.068L1265.31 137.726Z"
                      fill="#05F34A"
                      stroke="#1B1E1A"
                      strokeWidth="3"
                      strokeLinecap="square"
                      strokeLinejoin="round"
                    />
                    <path
                      id="Vector_14"
                      d="M1265.31 137.725L1295.61 102.015L1458.42 132.071L1265.31 137.725Z"
                      fill="#05F34A"
                      stroke="#1B1E1A"
                      strokeWidth="3"
                      strokeLinecap="square"
                      strokeLinejoin="round"
                    />
                  </g>
                </g>
                <path
                  id="path_2"
                  d="M973.861 226.794C1015.92 240.459 1041.39 136.212 1005.93 135.899C977.513 135.649 990.28 214.204 1046.61 229.17C1089.82 240.65 1168.88 147.886 1092.89 84.6262C1022.57 26.0944 1052.01 288.336 1197.12 209.704"
                  stroke="#0AE448"
                  strokeWidth="3"
                  strokeMiterlimit="10"
                />
                <path
                  className="sprinkle"
                  d="M1209.36 520.058C1200.77 520.837 1192.24 522.07 1183.8 523.845C1177.31 525.218 1179.8 535.21 1186.31 533.855C1193.88 532.265 1201.5 531.082 1209.21 530.374C1215.74 529.778 1215.98 519.458 1209.36 520.058Z"
                  fill="#BAA5F5"
                  stroke="#1B1E1A"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                />
                <path
                  className="sprinkle"
                  d="M816.237 177.713C818.964 185.892 822.12 193.912 825.782 201.719C828.608 207.725 837.761 203.006 834.952 196.986C831.668 189.98 828.767 182.829 826.312 175.491C824.233 169.269 814.133 171.4 816.237 177.713Z"
                  fill="#BAA5F5"
                  stroke="#1B1E1A"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                />
                <path
                  className="sprinkle"
                  d="M946.737 711.212C949.464 719.39 952.62 727.411 956.282 735.218C959.108 741.223 968.261 736.505 965.452 730.485C962.168 723.479 959.267 716.328 956.812 708.99C954.733 702.768 944.633 704.899 946.737 711.212Z"
                  fill="#B82C6F"
                  stroke="#1B1E1A"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                />
                <path
                  className="sprinkle"
                  d="M823.961 771.534C820.994 776.919 818.34 782.573 816.071 788.532C814.331 793.123 821.749 798.298 823.506 793.714C825.543 788.368 827.863 783.26 830.521 778.421C832.776 774.321 826.252 767.377 823.961 771.534Z"
                  fill="#FF783E"
                  stroke="#1B1E1A"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                />
                <path
                  className="sprinkle"
                  d="M1155.56 507.934C1152.64 507.082 1150.25 508.853 1149.16 511.444C1147.18 516.21 1145.2 520.99 1143.2 525.751C1142.12 528.327 1144.25 531.431 1146.71 532.149C1149.63 533.001 1152.02 531.229 1153.11 528.638C1155.09 523.873 1157.07 519.107 1159.07 514.331C1160.15 511.755 1158.02 508.651 1155.56 507.934Z"
                  fill="#FF783E"
                  stroke="#1B1E1A"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                />
                <path
                  className="sprinkle"
                  d="M816.771 232.868C816.612 235.909 818.885 237.83 821.654 238.29C826.748 239.127 831.856 239.965 836.948 240.817C839.703 241.276 842.237 238.494 842.371 235.933C842.53 232.892 840.257 230.971 837.487 230.511C832.394 229.674 827.3 228.837 822.193 227.984C819.438 227.525 816.905 230.306 816.771 232.868Z"
                  fill="#FF783E"
                  stroke="#1B1E1A"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                />
                <path
                  className="sprinkle"
                  d="M1155.78 596.226L1147.07 574.282C1146.03 571.668 1142.31 571.051 1140.07 572.243C1137.37 573.689 1137.01 576.679 1138.03 579.241C1140.95 586.559 1143.84 593.872 1146.74 601.186C1147.78 603.8 1151.51 604.417 1153.74 603.225C1156.45 601.778 1156.8 598.789 1155.78 596.226Z"
                  fill="#A6CFE7"
                  stroke="#1B1E1A"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                />
                <g id="hand">
                  <g id="Group 553">
                    <path
                      id="Vector_2"
                      d="M1162.52 666.144L1176.06 718.388C1177.72 724.799 1178.06 731.516 1176.85 738.115C1176.09 742.292 1174.65 746.79 1172.06 750.769C1172.76 751.621 1173.29 752.621 1173.58 753.738L1175.28 760.084C1176.48 764.609 1173.51 769.333 1168.64 770.633L1117.65 784.237C1112.77 785.537 1107.84 782.921 1106.63 778.396L1104.94 772.05C1104.59 770.737 1104.59 769.402 1104.89 768.145C1098.39 765.431 1093.48 760.261 1091.36 752.35C1091.36 752.35 1090.43 748.786 1089.73 746.166L1064.68 740.226C1058.44 738.717 1053.8 735.175 1054.22 729.35C1054.56 724.65 1056.69 721.15 1061.3 719.919C1061.31 719.916 1061.32 719.914 1061.34 719.91C1062.94 719.485 1064.63 719.486 1066.26 719.826L1083.68 723.457L1083.9 723.462C1083.77 723.132 1071.39 676.788 1071.39 676.788C1069.9 671.215 1073.37 665.234 1079.35 663.543C1082.43 662.673 1085.53 663.068 1088.05 664.403C1090.51 665.709 1092.41 667.918 1093.16 670.718L1101.74 701.364L1106.88 720.624C1107.2 721.82 1107.73 722.905 1108.42 723.86C1109.35 725.147 1110.58 726.191 1111.99 726.941C1114.46 728.247 1117.49 728.654 1120.5 727.85C1122.6 727.29 1124.42 726.216 1125.83 724.819C1128.47 722.2 1129.69 718.45 1128.72 714.799C1129.46 717.598 1131.36 719.807 1133.83 721.117C1134.6 721.523 1135.42 721.844 1136.29 722.07C1136.63 722.16 1136.98 722.231 1137.34 722.285L1137.34 722.284C1137.83 722.367 1138.33 722.416 1138.84 722.429C1139.98 722.469 1141.16 722.34 1142.34 722.024L1142.53 721.975C1142.65 721.939 1142.77 721.904 1142.89 721.863L1142.91 721.856C1143.06 721.809 1143.21 721.756 1143.36 721.703C1143.41 721.687 1143.46 721.669 1143.51 721.647C1143.59 721.618 1143.67 721.583 1143.76 721.548C1143.89 721.504 1144.01 721.451 1144.13 721.392C1144.15 721.391 1144.16 721.388 1144.17 721.379C1144.33 721.309 1144.49 721.237 1144.65 721.152C1149.27 718.847 1151.85 713.819 1150.56 708.974L1140.75 672.215C1139.27 666.641 1142.74 660.663 1148.71 658.97C1151.79 658.1 1154.9 658.494 1157.42 659.829C1159.88 661.135 1161.78 663.344 1162.53 666.146L1162.52 666.144Z"
                      fill="#FFFCE1"
                      stroke="black"
                      strokeWidth="4"
                      strokeMiterlimit="10"
                    />
                    <path
                      id="Vector_3"
                      d="M1150.49 708.729L1150.56 708.975C1151.85 713.82 1149.27 718.847 1144.65 721.152C1149.3 718.795 1151.79 713.605 1150.49 708.729Z"
                      fill="#FFFCE1"
                      stroke="black"
                      strokeWidth="4"
                      strokeMiterlimit="10"
                    />
                    <path
                      id="Vector_4"
                      d="M1083.9 723.464C1083.9 723.464 1083.51 722.576 1083.48 722.132C1083.48 722.132 1083.77 723.133 1083.9 723.464Z"
                      fill="#FFFCE1"
                      stroke="black"
                      strokeWidth="4"
                      strokeMiterlimit="10"
                    />
                    <path
                      id="Vector_5"
                      d="M1145.42 689.713L1150.56 708.972C1151.85 713.817 1149.27 718.844 1144.65 721.15C1144.49 721.232 1144.33 721.308 1144.16 721.377C1144.15 721.387 1144.14 721.389 1144.13 721.389C1144 721.449 1143.88 721.501 1143.75 721.546C1143.67 721.587 1143.59 721.616 1143.51 721.645C1143.46 721.664 1143.4 721.682 1143.36 721.701C1143.21 721.757 1143.06 721.807 1142.91 721.854L1142.88 721.861C1142.7 721.918 1142.52 721.973 1142.34 722.021L1142.34 722.022C1141.16 722.337 1139.98 722.467 1138.83 722.427C1138.33 722.413 1137.83 722.365 1137.34 722.285L1137.34 722.285C1136.98 722.225 1136.63 722.154 1136.29 722.068C1135.42 721.844 1134.6 721.521 1133.82 721.114C1131.36 719.805 1129.46 717.596 1128.71 714.797C1129.69 718.448 1128.46 722.199 1125.82 724.817C1124.41 726.214 1122.6 727.288 1120.5 727.848C1117.49 728.652 1114.46 728.247 1111.99 726.939C1110.58 726.191 1109.35 725.144 1108.42 723.86C1107.73 722.905 1107.2 721.82 1106.88 720.624L1101.74 701.362L1145.42 689.71L1145.42 689.713Z"
                      fill="#FFFCE1"
                      stroke="black"
                      strokeWidth="4"
                      strokeMiterlimit="10"
                    />
                    <path
                      id="Vector_6"
                      d="M1145.42 689.713L1150.56 708.972C1151.85 713.817 1149.27 718.844 1144.65 721.15C1144.49 721.232 1144.33 721.308 1144.16 721.377C1144.15 721.387 1144.14 721.389 1144.13 721.389C1144 721.449 1143.88 721.501 1143.75 721.546C1143.67 721.587 1143.59 721.616 1143.51 721.645C1143.46 721.664 1143.4 721.682 1143.36 721.701C1143.21 721.757 1143.06 721.807 1142.91 721.854L1142.88 721.861C1142.7 721.918 1142.52 721.973 1142.34 722.021L1142.34 722.022C1141.16 722.337 1139.98 722.467 1138.83 722.427C1138.33 722.413 1137.83 722.365 1137.34 722.285L1137.34 722.285C1136.98 722.225 1136.63 722.154 1136.29 722.068C1135.42 721.844 1134.6 721.521 1133.82 721.114C1131.36 719.805 1129.46 717.596 1128.71 714.797L1123.57 695.537C1122.08 689.938 1125.76 684.094 1131.79 682.486C1134.8 681.68 1137.84 682.087 1140.3 683.394C1142.77 684.703 1144.67 686.912 1145.41 689.711L1145.42 689.713Z"
                      fill="#FFFCE1"
                      stroke="black"
                      strokeWidth="4"
                      strokeMiterlimit="10"
                    />
                    <path
                      id="Vector_7"
                      d="M1096.4 727.669C1096.4 727.669 1132.72 709.703 1142.25 745.402"
                      stroke="black"
                      strokeWidth="4"
                      strokeMiterlimit="10"
                    />
                    <path
                      id="Vector_8"
                      d="M1122.34 690.885L1128.72 714.799C1129.69 718.45 1128.46 722.201 1125.83 724.819C1124.42 726.216 1122.6 727.291 1120.5 727.85C1117.49 728.655 1114.46 728.247 1111.99 726.942C1110.58 726.191 1109.35 725.144 1108.42 723.86C1107.73 722.905 1107.2 721.82 1106.88 720.624L1100.5 696.71C1099.01 691.111 1102.69 685.267 1108.72 683.659C1111.73 682.854 1114.76 683.262 1117.23 684.567C1119.69 685.877 1121.59 688.086 1122.34 690.885Z"
                      fill="#FFFCE1"
                      stroke="black"
                      strokeWidth="4"
                      strokeMiterlimit="10"
                    />
                  </g>
                </g>
                <g id="main">
                  <image
                    href="https://assets.codepen.io/16327/3D-poly.png"
                    x="758"
                    y="320"
                    width="344"
                    height="370"
                  />
                </g>
              </g>
              <defs>
                <linearGradient
                  id="paint0_linear_6028_4729"
                  x1="341.328"
                  y1="165.489"
                  x2="1348.16"
                  y2="553.463"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0.427083" stopColor="#FF8709" />
                  <stop offset="0.791667" stopColor="#F7BDF8" />
                </linearGradient>
                <linearGradient
                  id="paint1_linear_6028_4729"
                  x1="695.52"
                  y1="382.901"
                  x2="1221.75"
                  y2="474.027"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0.0242443" stopColor="#0A157A" />
                  <stop offset="0.93898" stopColor="#9D95FF" />
                </linearGradient>
                <linearGradient
                  id="paint2_linear_6028_4729"
                  x1="1215.64"
                  y1="306.624"
                  x2="1025.13"
                  y2="256.799"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0.26957" stopColor="#FEC5FB" />
                  <stop offset="0.838196" stopColor="#00BAE2" />
                </linearGradient>
                <pattern
                  id="pattern"
                  patternContentUnits="objectBoundingBox"
                  width="0.625"
                  height="0.625"
                >
                  <use xlinkHref="#svg-noise" transform="scale(0.00125)"></use>
                </pattern>
                <image
                  id="svg-noise"
                  width="500"
                  height="500"
                  xlinkHref="https://assets.codepen.io/16327/noise-e82662fe.png"
                ></image>
              </defs>
            </svg>
          </div>
          <div className={styles.confetti}>
            <img
              alt="confetti circles"
              src="https://assets.codepen.io/16327/2D-circles.png"
            />
            <img
              alt="confetti keyframe"
              src="https://assets.codepen.io/16327/2D-keyframe.png"
            />
            <img
              alt="confetti lightning"
              src="https://assets.codepen.io/16327/2D-lightning.png"
            />
            <img
              alt="confetti star"
              src="https://assets.codepen.io/16327/2D-star.png"
            />
            <img
              alt="confetti flower"
              src="https://assets.codepen.io/16327/2D-flower.png"
            />
            <img
              alt="confetti cone"
              src="https://assets.codepen.io/16327/3D-cone.png"
            />
            <img
              alt="confetti spiral"
              src="https://assets.codepen.io/16327/3D-spiral.png"
            />
            <img
              alt="confetti spiral 2"
              src="https://assets.codepen.io/16327/3D-spiral.png"
            />
            <img
              alt="confetti tunnel"
              src="https://assets.codepen.io/16327/3D-tunnel.png"
            />
            <img
              alt="confetti hoop"
              src="https://assets.codepen.io/16327/3D-hoop.png"
            />
            <img
              alt="confetti semi"
              src="https://assets.codepen.io/16327/3D-semi.png"
            />
          </div>
        </div>
      )}
      <div className={styles.content}>
        <nav className={styles.navbar}>
          <div className={styles.navContent}>
            <div className={styles.hamburger}>
              <SVGHamburger
                isOpen={mobileMenuOpen}
                onClick={() => setMobileMenuOpen((v) => !v)}
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              />
            </div>
            <div className={styles.navLinks}>
              <button
                onClick={scrollToFeatures}
                className={`${styles.navLink} ${styles.btnAnimateChars}`}
              >
                <span data-button-animate-chars>Features</span>
              </button>
              <Link
                to="/pricing"
                className={`${styles.navLink} ${styles.btnAnimateChars}`}
              >
                <span data-button-animate-chars>Pricing</span>
              </Link>
              <Link
                to="/about"
                className={`${styles.navLink} ${styles.btnAnimateChars}`}
              >
                <span data-button-animate-chars>About</span>
              </Link>
              <Link
                to="/contact"
                className={`${styles.navLink} ${styles.btnAnimateChars}`}
              >
                <span data-button-animate-chars>Contact</span>
              </Link>
              {isAuthenticated ? (
                <>
                  <Link
                    to="/home"
                    className={`${styles.navLink} ${styles.btnAnimateChars}`}
                  >
                    <span data-button-animate-chars>Dashboard</span>
                  </Link>
                  <Link
                    to="/account"
                    className={`${styles.navLink} ${styles.btnAnimateChars}`}
                  >
                    <span data-button-animate-chars>Account</span>
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className={`${styles.loginLink} ${styles.btnAnimateChars}`}
                  >
                    <span data-button-animate-chars>Log out</span>
                    <LogOut className={styles.signOutIcon} size={16} />
                  </button>
                </>
              ) : (
                <Link
                  to="/auth"
                  className={`${styles.loginLink} ${styles.btnAnimateChars}`}
                >
                  <span data-button-animate-chars>Log in</span>
                </Link>
              )}
            </div>
            <Link to="/" className={styles.logo} aria-label="Home">
              <ol className={styles.logoBList} aria-hidden="true">
                <li className={styles.b}>
                  <div>
                    <span aria-hidden="true"></span>
                  </div>
                </li>
              </ol>
            </Link>
          </div>
          {
            <div
              className={styles.mobileMenu}
              style={{
                maxHeight:
                  mobileMenuOpen && mobileMenuContentRef.current
                    ? mobileMenuContentRef.current.scrollHeight + "px"
                    : 0,
              }}
            >
              <div
                ref={mobileMenuContentRef}
                className={styles.mobileMenuInner}
              >
                <button
                  onClick={() => {
                    scrollToFeatures();
                    setMobileMenuOpen(false);
                  }}
                  className={styles.mobileNavLink}
                >
                  Features
                </button>
                <Link
                  to="/pricing"
                  className={styles.mobileNavLink}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Pricing
                </Link>
                <Link
                  to="/about"
                  className={styles.mobileNavLink}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  About
                </Link>
                <Link
                  to="/contact"
                  className={styles.mobileNavLink}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Contact
                </Link>
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/home"
                      className={styles.mobileNavLink}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/account"
                      className={styles.mobileNavLink}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Account
                    </Link>
                    <button
                      onClick={() => {
                        handleSignOut();
                        setMobileMenuOpen(false);
                      }}
                      className={styles.mobileNavLink}
                    >
                      Log out
                    </button>
                  </>
                ) : (
                  <Link
                    to="/auth"
                    className={styles.mobileNavLink}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Log in
                  </Link>
                )}
              </div>
            </div>
          }
        </nav>

        <header ref={heroRef} className={`${styles.header} ${styles.heroBg}`}>
          <div className={styles.heroContent}>
            <motion.div
              className={styles.titleWrapper}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              variants={staggerContainer}
            >
              <motion.p className={styles.heroEyebrow} variants={fadeUp}>
                Bramlo
              </motion.p>
              <motion.h1 className={styles.heroMission} variants={fadeUp}>
                The new way to study. Finally here.
              </motion.h1>
              <motion.p
                className={`${styles.subtitle} ${styles.subtitleAnimated}`}
                variants={fadeUpFast}
              >
                Ever felt stuck — like you wanted to do better, but didn't know
                where to start? Bramlo gives you the full system: notes,
                quizzes, essays, and exam prep in one connected workspace.
              </motion.p>
              <motion.div className={styles.heroButtons} variants={fadeUp}>
                <button
                  onClick={scrollToDemo}
                  className={`${styles.primaryButton} ${styles.btnAnimateChars}`}
                >
                  <span data-button-animate-chars>Meet Lumi</span>{" "}
                  <FaArrowRight />
                </button>
                <a
                  href={foundingOfferLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${styles.secondaryButton} ${styles.btnAnimateChars}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handlePlanClick("monthly");
                  }}
                >
                  <span data-button-animate-chars>Get $9.99 Student Plan</span>{" "}
                  <FaArrowRight />
                </a>
              </motion.div>
              <motion.p
                className={styles.heroOfferNote}
                variants={fadeUpFast}
                style={{ marginTop: "0.5rem" }}
              >
                Next 500 spots at $9.99/month · Cancel anytime
              </motion.p>

              <motion.div
                className={styles.heroSignal}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.35, ease: "easeOut" }}
              >
                <div className={styles.heroSignalTicker}>
                  {useSimpleHeroSignal ? (
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={heroOutcomeIndex}
                        className={styles.heroSignalMessage}
                        aria-live="polite"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.28, ease: "easeInOut" }}
                      >
                        {heroOutcomeMessages[heroOutcomeIndex]}
                      </motion.span>
                    </AnimatePresence>
                  ) : (
                    <span
                      key={heroOutcomeIndex}
                      className={styles.heroSignalMessage}
                      aria-live="polite"
                    >
                      {Array.from(heroOutcomeMessages[heroOutcomeIndex]).map(
                        (ch, idx) => (
                          <span
                            key={`${heroOutcomeIndex}-${idx}-${ch}`}
                            ref={(el) => {
                              heroSignalLettersRef.current[idx] = el;
                            }}
                            className={
                              ch === " "
                                ? styles.heroSignalSpace
                                : styles.heroSignalChar
                            }
                          >
                            {ch === " " ? "\u00A0" : ch}
                          </span>
                        ),
                      )}
                    </span>
                  )}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </header>

        <div style={{ display: "flex", flexDirection: "column" }}>
          <section
            className={`${styles.section} ${styles.heroBg}`}
            style={{ order: 4 }}
          >
            <motion.div
              className={styles.flowShowcaseInner}
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              style={{
                maxWidth: 1180,
                margin: "0 auto",
                padding: "0 16px",
                display: "flex",
                flexDirection: "column",
                gap: "1.35rem",
              }}
            >
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={viewport}
                className={styles.wsInner}
                style={{ gap: "0.5rem", marginBottom: "0.35rem" }}
              >
                <motion.div className={styles.wsBadge} variants={fadeUp}>
                  <FiLayout size={11} /> Study Flow
                </motion.div>

                <motion.h2 className={styles.wsHeadline} variants={clipReveal}>
                  {"From overwhelmed to".split(" ").map((word, i, arr) => (
                    <motion.span
                      key={i}
                      variants={wordReveal}
                      style={{ display: "inline-block" }}
                    >
                      {word}
                      {i < arr.length - 1 ? "\u00A0" : ""}
                    </motion.span>
                  ))}{" "}
                  <span className={styles.wsAccent}>exam-ready.</span>
                </motion.h2>

                <motion.p
                  className={styles.wsSubtitle}
                  variants={fadeUp}
                  style={{ color: "#f4f8ff", opacity: 1 }}
                >
                  One workflow that turns your raw notes into a study plan, quiz
                  set, and essay draft — before you've had a chance to
                  procrastinate.
                </motion.p>
              </motion.div>

              <motion.div
                className={styles.flowShowcaseCard}
                variants={fadeUp}
                style={{
                  border: "1px solid rgba(255,255,255,0.24)",
                  borderRadius: 20,
                  background:
                    "linear-gradient(160deg, rgba(255,255,255,0.13) 0%, rgba(255,255,255,0.06) 60%, rgba(255,255,255,0.03) 100%)",
                  boxShadow:
                    "0 24px 58px rgba(0,0,0,0.3), 0 1px 0 rgba(255,255,255,0.14) inset",
                  overflow: "hidden",
                  display: "grid",
                  gridTemplateColumns:
                    "minmax(280px, 1fr) minmax(340px, 1.5fr)",
                  gap: "1rem",
                  padding: "1rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                    padding: "0.35rem",
                  }}
                >
                  <motion.div
                    variants={fadeUp}
                    style={{
                      borderRadius: 14,
                      border: "1px solid rgba(255,255,255,0.26)",
                      background:
                        "linear-gradient(170deg, rgba(39,49,74,0.62), rgba(22,28,43,0.58))",
                      padding: "0.85rem",
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.6rem",
                      boxShadow: "0 14px 30px rgba(15,23,42,0.35)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.75rem",
                          color: "rgba(236,243,255,0.95)",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          fontWeight: 700,
                        }}
                      >
                        Current step
                      </span>
                      <span />
                    </div>

                    <h4
                      style={{
                        margin: 0,
                        color: "#ffffff",
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        lineHeight: 1.25,
                      }}
                    >
                      {rescueScenes[rescueSceneIndex].title}
                    </h4>
                    <p
                      style={{
                        margin: 0,
                        color: "rgba(245,248,255,0.98)",
                        fontSize: "0.86rem",
                        lineHeight: 1.45,
                      }}
                    >
                      {rescueScenes[rescueSceneIndex].subtitle}
                    </p>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        gap: "0.5rem",
                      }}
                    >
                      {[
                        { label: "Session length", value: "~35 min" },
                        { label: "Knowledge checks", value: "Included" },
                      ].map((m) => (
                        <div
                          key={m.label}
                          style={{
                            borderRadius: 10,
                            border: "1px solid rgba(255,255,255,0.2)",
                            background: "rgba(255,255,255,0.09)",
                            padding: "0.55rem",
                          }}
                        >
                          <p
                            style={{
                              margin: 0,
                              color: "rgba(242,247,255,0.97)",
                              fontSize: "0.72rem",
                            }}
                          >
                            {m.label}
                          </p>
                          <p
                            style={{
                              margin: "0.25rem 0 0",
                              color: "#ffffff",
                              fontWeight: 700,
                              fontSize: "0.88rem",
                            }}
                          >
                            {m.value}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div
                      style={{
                        borderRadius: 10,
                        border: "1px dashed rgba(255,255,255,0.3)",
                        background: "rgba(255,255,255,0.09)",
                        padding: "0.55rem 0.65rem",
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          color: "rgba(244,248,255,0.98)",
                          fontSize: "0.72rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.07em",
                          fontWeight: 700,
                        }}
                      >
                        Session note
                      </p>
                      <AnimatePresence mode="wait">
                        <motion.p
                          key={rescueSceneIndex}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.18 }}
                          style={{
                            margin: "0.35rem 0 0",
                            color: "#ffffff",
                            fontSize: "0.82rem",
                            lineHeight: 1.42,
                          }}
                        >
                          {rescueScenes[rescueSceneIndex].transcript}
                        </motion.p>
                      </AnimatePresence>
                    </div>
                  </motion.div>

                  <div
                    style={{
                      display: "flex",
                      gap: "0.55rem",
                      flexWrap: "wrap",
                    }}
                  >
                    {rescueScenes.map((scene, idx) => (
                      <button
                        type="button"
                        key={scene.label}
                        onClick={() => setRescueSceneIndex(idx)}
                        style={{
                          borderRadius: 999,
                          border:
                            idx === rescueSceneIndex
                              ? `1px solid ${scene.color}`
                              : "1px solid rgba(255,255,255,0.18)",
                          background:
                            idx === rescueSceneIndex
                              ? `${scene.color}22`
                              : "rgba(255,255,255,0.04)",
                          color: "#fff",
                          fontSize: "0.74rem",
                          fontWeight: 700,
                          padding: "7px 11px",
                          cursor: "pointer",
                        }}
                      >
                        {scene.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div
                  style={{
                    borderRadius: 16,
                    border: "1px solid rgba(255,255,255,0.14)",
                    background: "rgba(5,6,12,0.72)",
                    overflow: "hidden",
                    position: "relative",
                    minHeight: 360,
                  }}
                >
                  <div
                    style={{
                      padding: "0.9rem",
                      display: "grid",
                      gap: "0.8rem",
                    }}
                  >
                    <div
                      className={styles.flowVizBox}
                      style={{
                        borderRadius: 14,
                        border: "1px solid rgba(255,255,255,0.12)",
                        minHeight: 300,
                        overflow: "hidden",
                        position: "relative",
                        background:
                          "linear-gradient(135deg, rgba(30,41,59,0.65), rgba(17,24,39,0.95))",
                      }}
                    >
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={rescueSceneIndex}
                          initial={{
                            opacity: 0,
                            scale: 1.02,
                            filter: "blur(6px)",
                          }}
                          animate={{
                            opacity: 1,
                            scale: 1,
                            filter: "blur(0px)",
                          }}
                          exit={{
                            opacity: 0,
                            scale: 0.98,
                            filter: "blur(4px)",
                          }}
                          transition={{
                            duration: 0.3,
                            ease: [0.23, 1, 0.32, 1],
                          }}
                          style={{ position: "absolute", inset: 0 }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              inset: "-30% -20% auto auto",
                              width: 260,
                              height: 260,
                              borderRadius: "50%",
                              background: `radial-gradient(circle, ${rescueScenes[rescueSceneIndex].glow} 0%, transparent 68%)`,
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              inset: "auto auto -35% -18%",
                              width: 230,
                              height: 230,
                              borderRadius: "50%",
                              background: `radial-gradient(circle, ${rescueScenes[rescueSceneIndex].color}33 0%, transparent 72%)`,
                            }}
                          />

                          <div
                            style={{
                              position: "absolute",
                              inset: 0,
                              padding: "1rem",
                              display: "grid",
                              gridTemplateRows: "auto 1fr auto",
                              gap: "0.7rem",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "0.72rem",
                                  letterSpacing: "0.08em",
                                  textTransform: "uppercase",
                                  fontWeight: 700,
                                  color: "rgba(255,255,255,0.72)",
                                }}
                              >
                                {rescueScenes[rescueSceneIndex].label}
                              </span>
                              <span
                                style={{
                                  borderRadius: 999,
                                  border: `1px solid ${rescueScenes[rescueSceneIndex].color}`,
                                  color: "#fff",
                                  padding: "4px 9px",
                                  fontSize: "0.72rem",
                                  fontWeight: 700,
                                  background: `${rescueScenes[rescueSceneIndex].color}22`,
                                }}
                              >
                                {rescueScenes[rescueSceneIndex].chip}
                              </span>
                            </div>

                            <div
                              className={styles.flowVizGrid}
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "0.7rem",
                                alignItems: "stretch",
                              }}
                            >
                              <div
                                style={{
                                  borderRadius: 12,
                                  border: "1px solid rgba(255,255,255,0.14)",
                                  background: "rgba(255,255,255,0.04)",
                                  padding: "0.75rem",
                                  display: "grid",
                                  alignContent: "start",
                                  gap: "0.45rem",
                                }}
                              >
                                <p
                                  style={{
                                    margin: 0,
                                    color: "#fff",
                                    fontWeight: 700,
                                    fontSize: "0.88rem",
                                  }}
                                >
                                  Coverage by unit
                                </p>
                                {rescueScenes[
                                  rescueSceneIndex
                                ].moduleProgress.map((v, idx) => (
                                  <div key={idx}>
                                    <p
                                      style={{
                                        margin: "0 0 4px",
                                        fontSize: "0.68rem",
                                        color: "rgba(255,255,255,0.58)",
                                      }}
                                    >
                                      {rescueScenes[rescueSceneIndex]
                                        .unitLabels?.[idx] || `Unit ${idx + 1}`}
                                    </p>
                                    <div
                                      style={{
                                        height: 5,
                                        borderRadius: 99,
                                        background: "rgba(255,255,255,0.12)",
                                        overflow: "hidden",
                                      }}
                                    >
                                      <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${v}%` }}
                                        transition={{
                                          duration: 0.5,
                                          delay: 0.08 + idx * 0.05,
                                        }}
                                        style={{
                                          height: "100%",
                                          borderRadius: 99,
                                          background: `linear-gradient(90deg, ${rescueScenes[rescueSceneIndex].color}, #fff)`,
                                        }}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <div
                                style={{
                                  borderRadius: 12,
                                  border: "1px solid rgba(255,255,255,0.14)",
                                  background: "rgba(255,255,255,0.03)",
                                  padding: "0.75rem",
                                  display: "grid",
                                  alignContent: "start",
                                  gap: "0.45rem",
                                }}
                              >
                                <p
                                  style={{
                                    margin: 0,
                                    color: "rgba(242,247,255,0.9)",
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  What Lumi built
                                </p>
                                <AnimatePresence mode="wait">
                                  <motion.div
                                    key={rescueSceneIndex}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.2 }}
                                    style={{
                                      display: "grid",
                                      gap: "0.45rem",
                                      marginTop: "0.35rem",
                                    }}
                                  >
                                    {rescueScenes[
                                      rescueSceneIndex
                                    ].artifacts.map((artifact) => (
                                      <div
                                        key={artifact.label}
                                        style={{
                                          borderRadius: 8,
                                          border:
                                            "1px solid rgba(255,255,255,0.14)",
                                          background: "rgba(255,255,255,0.06)",
                                          padding: "0.42rem 0.48rem",
                                        }}
                                      >
                                        <p
                                          style={{
                                            margin: 0,
                                            color: "rgba(239,245,255,0.92)",
                                            fontSize: "0.66rem",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.05em",
                                            fontWeight: 700,
                                          }}
                                        >
                                          {artifact.label}
                                        </p>
                                        <p
                                          style={{
                                            margin: "0.22rem 0 0",
                                            color: "#fff",
                                            fontSize: "0.75rem",
                                            lineHeight: 1.35,
                                          }}
                                        >
                                          {artifact.value}
                                        </p>
                                      </div>
                                    ))}
                                  </motion.div>
                                </AnimatePresence>
                              </div>
                            </div>

                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr auto",
                                alignItems: "center",
                                gap: "0.65rem",
                              }}
                            >
                              <div
                                style={{
                                  height: 5,
                                  borderRadius: 999,
                                  background: "rgba(255,255,255,0.18)",
                                  overflow: "hidden",
                                }}
                              >
                                <motion.div
                                  key={rescueSceneIndex}
                                  initial={{ width: 0 }}
                                  animate={{ width: "100%" }}
                                  transition={{
                                    duration: 2,
                                    ease: "linear",
                                  }}
                                  style={{
                                    height: "100%",
                                    borderRadius: 999,
                                    background: `linear-gradient(90deg, ${rescueScenes[rescueSceneIndex].color}, #f8fafc)`,
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      </AnimatePresence>
                    </div>

                    <div
                      className={styles.flowStepRow}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        alignItems: "center",
                        gap: "0.75rem",
                      }}
                    >
                      <div
                        className={styles.flowStepChips}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                          gap: "0.4rem",
                        }}
                      >
                        {rescueScenes.map((scene, idx) => (
                          <div
                            key={scene.label}
                            style={{
                              borderRadius: 9,
                              border:
                                idx === rescueSceneIndex
                                  ? `1px solid ${scene.color}`
                                  : "1px solid rgba(255,255,255,0.12)",
                              background:
                                idx === rescueSceneIndex
                                  ? `${scene.color}1f`
                                  : "rgba(255,255,255,0.04)",
                              padding: "0.38rem 0.42rem",
                            }}
                          >
                            <span
                              style={{
                                display: "block",
                                width: "100%",
                                height: 4,
                                borderRadius: 99,
                                marginBottom: 6,
                                background: scene.color,
                                opacity: idx === rescueSceneIndex ? 1 : 0.45,
                              }}
                            />
                            <span
                              style={{
                                fontSize: "0.67rem",
                                color: "#fff",
                                fontWeight: 700,
                                opacity: idx === rescueSceneIndex ? 1 : 0.75,
                              }}
                            >
                              {scene.label}
                            </span>
                          </div>
                        ))}
                      </div>
                      <span
                        style={{
                          color: "rgba(255,255,255,0.72)",
                          fontSize: "0.72rem",
                          whiteSpace: "nowrap",
                          fontWeight: 600,
                        }}
                      >
                        Updates automatically
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                variants={fadeUp}
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
                  gap: "0.75rem",
                }}
              >
                {[
                  {
                    title: "What gets built",
                    text: "Organized notes, quiz sets, and revision prompts from the same source material.",
                  },
                  {
                    title: "Why it works",
                    text: "Each step connects to the next, so you keep context instead of starting over.",
                  },
                  {
                    title: "What you feel",
                    text: "Less scattered studying, clearer priorities, and better confidence before exams.",
                  },
                  {
                    title: "What updates live",
                    text: "Weak-topic priority, quiz difficulty, and draft quality refresh every cycle.",
                  },
                  {
                    title: "What you keep",
                    text: "A reusable study system you can run again for every chapter and every class.",
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    style={{
                      borderRadius: 14,
                      border: "1px solid rgba(255,255,255,0.16)",
                      background: "rgba(255,255,255,0.09)",
                      padding: "0.75rem 0.85rem",
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        color: "#f8fbff",
                        fontSize: "0.82rem",
                        fontWeight: 700,
                      }}
                    >
                      {item.title}
                    </p>
                    <p
                      style={{
                        margin: "0.35rem 0 0",
                        color: "rgba(235,241,255,0.9)",
                        fontSize: "0.8rem",
                        lineHeight: 1.42,
                      }}
                    >
                      {item.text}
                    </p>
                  </div>
                ))}
              </motion.div>

              <motion.div
                variants={fadeUp}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 2,
                }}
              >
                <Link
                  to="/auth"
                  className={`${styles.primaryButton} ${styles.darkButton} ${styles.btnAnimateChars}`}
                >
                  <span data-button-animate-chars>Start This Workflow</span>
                  <FaArrowRight />
                </Link>
              </motion.div>
            </motion.div>
          </section>

          {/* ── Workspace Spotlight ─────────────────────────────────────── */}
          <section
            className={`${styles.section} ${styles.heroBg} ${styles.workspaceSplash}`}
            style={{ order: 3 }}
          >
            {/* Ambient background orbs */}
            <div className={styles.wsOrb1} />
            <div className={styles.wsOrb2} />
            <div className={styles.wsOrb3} />

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className={styles.wsInner}
            >
              {/* Badge */}
              <motion.div className={styles.wsBadge} variants={fadeUp}>
                <FiLayout size={11} /> Study Workspace — New
              </motion.div>

              {/* Headline */}
              <motion.h2 className={styles.wsHeadline} variants={clipReveal}>
                {"Your Entire Study Life, ".split(" ").map((word, i, arr) => (
                  <motion.span
                    key={i}
                    variants={wordReveal}
                    style={{ display: "inline-block" }}
                  >
                    {word}
                    {i < arr.length - 1 ? "\u00A0" : ""}
                  </motion.span>
                ))}
                <span className={styles.wsAccent}>One Canvas.</span>
              </motion.h2>

              {/* Subtitle */}
              <motion.p className={styles.wsSubtitle} variants={fadeUp}>
                Deadlines next to your essay draft. Mind maps beside your
                sources. Lumi on every card. This is what it looks like when
                nothing falls through the cracks.
              </motion.p>

              {/* Social proof */}
              <motion.div className={styles.wsSocialProof} variants={fadeUp}>
                <span className={styles.wsStars}>★★★★★</span>
                <span>
                  Used by <strong>297K+</strong> students worldwide
                </span>
              </motion.div>

              {/* Canvas Mockup */}
              <motion.div className={styles.wsCanvasWrap} variants={fadeUp}>
                {/* Ambient glow behind the mock */}
                <div className={styles.wsCanvasGlow} />

                <div className={styles.wsCanvasMock}>
                  {/* Toolbar — OS-style with traffic lights */}
                  <div className={styles.wsTopBar}>
                    <div className={styles.wsTopBarLeft}>
                      <div className={styles.wsTrafficLights}>
                        <span
                          className={styles.wsLight}
                          style={{ background: "#FF5F57" }}
                        />
                        <span
                          className={styles.wsLight}
                          style={{ background: "#FEBC2E" }}
                        />
                        <span
                          className={styles.wsLight}
                          style={{ background: "#28C840" }}
                        />
                      </div>
                      <span className={styles.wsTopBarTitle}>
                        Study Workspace
                      </span>
                      <span className={styles.wsItemCount}>6 cards</span>
                    </div>
                    <div className={styles.wsTopBarRight}>
                      <span className={styles.wsSaved}>
                        <span className={styles.wsSavedDot} /> Saved
                      </span>
                      {["V", "H", "C", "−", "100%", "+"].map((t, i) => (
                        <span key={i} className={styles.wsTopBarTool}>
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Canvas body */}
                  <div className={styles.wsCanvasInner}>
                    {/* Animated SVG connection lines */}
                    <svg
                      style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        pointerEvents: "none",
                        overflow: "visible",
                      }}
                    >
                      <motion.path
                        d="M 258 68 C 220 90 190 110 157 132"
                        fill="none"
                        stroke="#D97706"
                        strokeWidth="1.5"
                        strokeDasharray="5,3"
                        strokeLinecap="round"
                        initial={{ pathLength: 0, opacity: 0 }}
                        whileInView={{ pathLength: 1, opacity: 0.65 }}
                        viewport={{ once: true }}
                        transition={{
                          delay: 0.5,
                          duration: 0.4,
                          ease: "easeInOut",
                        }}
                      />
                      <motion.path
                        d="M 258 68 C 275 100 295 112 302 136"
                        fill="none"
                        stroke="#D97706"
                        strokeWidth="1.5"
                        strokeDasharray="5,3"
                        strokeLinecap="round"
                        initial={{ pathLength: 0, opacity: 0 }}
                        whileInView={{ pathLength: 1, opacity: 0.65 }}
                        viewport={{ once: true }}
                        transition={{
                          delay: 0.6,
                          duration: 0.4,
                          ease: "easeInOut",
                        }}
                      />
                      <motion.path
                        d="M 258 68 C 340 38 415 48 474 68"
                        fill="none"
                        stroke="#6366f1"
                        strokeWidth="1.5"
                        strokeDasharray="5,3"
                        strokeLinecap="round"
                        initial={{ pathLength: 0, opacity: 0 }}
                        whileInView={{ pathLength: 1, opacity: 0.55 }}
                        viewport={{ once: true }}
                        transition={{
                          delay: 0.7,
                          duration: 0.5,
                          ease: "easeInOut",
                        }}
                      />
                      <motion.path
                        d="M 393 188 C 370 218 340 236 296 256"
                        fill="none"
                        stroke="#16A34A"
                        strokeWidth="1.5"
                        strokeDasharray="5,3"
                        strokeLinecap="round"
                        initial={{ pathLength: 0, opacity: 0 }}
                        whileInView={{ pathLength: 1, opacity: 0.55 }}
                        viewport={{ once: true }}
                        transition={{
                          delay: 0.8,
                          duration: 0.55,
                          ease: "easeInOut",
                        }}
                      />
                      <motion.path
                        d="M 157 192 C 140 220 110 240 84 258"
                        fill="none"
                        stroke="#D97706"
                        strokeWidth="1.5"
                        strokeDasharray="5,3"
                        strokeLinecap="round"
                        initial={{ pathLength: 0, opacity: 0 }}
                        whileInView={{ pathLength: 1, opacity: 0.5 }}
                        viewport={{ once: true }}
                        transition={{
                          delay: 0.85,
                          duration: 0.5,
                          ease: "easeInOut",
                        }}
                      />
                    </svg>

                    {/* ── Thesis — blue, center-top ── */}
                    <motion.div
                      className={styles.wsFakeCard}
                      style={{
                        left: 152,
                        top: 18,
                        width: 198,
                        borderColor: "#2563EB44",
                        background: "#EFF6FF",
                      }}
                      initial={{ opacity: 0, y: 16, scale: 0.92 }}
                      whileInView={{ opacity: 1, y: 0, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{
                        delay: 0.2,
                        duration: 0.4,
                        ease: [0.23, 1, 0.32, 1],
                      }}
                    >
                      <div
                        className={styles.wsFakeCardHeader}
                        style={{ background: "#2563EB" }}
                      >
                        Thesis Statement
                      </div>
                      <div
                        className={styles.wsFakeCardBody}
                        style={{ color: "#1e3a8a" }}
                      >
                        Renewable energy adoption is accelerating due to falling
                        costs and policy pressure.
                      </div>
                    </motion.div>

                    {/* ── Deadline — red, right ── */}
                    <motion.div
                      className={styles.wsFakeCard}
                      style={{
                        left: 424,
                        top: 18,
                        width: 152,
                        borderColor: "#DC262644",
                        background: "#FEF2F2",
                      }}
                      initial={{ opacity: 0, x: 16, scale: 0.92 }}
                      whileInView={{ opacity: 1, x: 0, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{
                        delay: 0.35,
                        duration: 0.4,
                        ease: [0.23, 1, 0.32, 1],
                      }}
                    >
                      <div
                        className={styles.wsFakeCardHeader}
                        style={{ background: "#DC2626", gap: 5 }}
                      >
                        <FiClock size={9} /> Deadline
                      </div>
                      <div
                        style={{
                          textAlign: "center",
                          padding: "9px 10px 11px",
                        }}
                      >
                        <p
                          style={{
                            margin: 0,
                            fontSize: 10,
                            fontWeight: 700,
                            color: "#DC2626",
                          }}
                        >
                          Essay Final Draft
                        </p>
                        <p
                          style={{
                            margin: "5px 0 2px",
                            fontSize: 25,
                            fontWeight: 900,
                            color: "#DC2626",
                            lineHeight: 1,
                          }}
                        >
                          2d left
                        </p>
                        <p
                          style={{
                            margin: 0,
                            fontSize: 9,
                            color: "#DC2626",
                            opacity: 0.55,
                          }}
                        >
                          Fri, Apr 18
                        </p>
                      </div>
                    </motion.div>

                    {/* ── Argument — yellow, left ── */}
                    <motion.div
                      className={styles.wsFakeCard}
                      style={{
                        left: 22,
                        top: 132,
                        width: 152,
                        borderColor: "#D9770644",
                        background: "#FFFBEB",
                      }}
                      initial={{ opacity: 0, x: -16, scale: 0.92 }}
                      whileInView={{ opacity: 1, x: 0, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{
                        delay: 0.5,
                        duration: 0.4,
                        ease: [0.23, 1, 0.32, 1],
                      }}
                    >
                      <div
                        className={styles.wsFakeCardHeader}
                        style={{ background: "#D97706" }}
                      >
                        Argument 1
                      </div>
                      <div
                        className={styles.wsFakeCardBody}
                        style={{ color: "#78350F" }}
                      >
                        Solar costs dropped 90% over the last decade.
                      </div>
                    </motion.div>

                    {/* ── Checklist — green, center ── */}
                    <motion.div
                      className={styles.wsFakeCard}
                      style={{
                        left: 210,
                        top: 136,
                        width: 182,
                        borderColor: "#16A34A44",
                        background: "#F0FDF4",
                      }}
                      initial={{ opacity: 0, y: 16, scale: 0.92 }}
                      whileInView={{ opacity: 1, y: 0, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{
                        delay: 0.62,
                        duration: 0.4,
                        ease: [0.23, 1, 0.32, 1],
                      }}
                    >
                      <div
                        className={styles.wsFakeCardHeader}
                        style={{ background: "#16A34A" }}
                      >
                        3 / 4 done
                      </div>
                      <div className={styles.wsFakeProgress}>
                        <motion.div
                          className={styles.wsFakeProgressBar}
                          style={{ background: "#16A34A" }}
                          initial={{ width: "0%" }}
                          whileInView={{ width: "75%" }}
                          viewport={{ once: true }}
                          transition={{
                            delay: 1.1,
                            duration: 1.1,
                            ease: "easeOut",
                          }}
                        />
                      </div>
                      <div className={styles.wsFakeCardBody}>
                        {[
                          ["Read Chapter 4", true],
                          ["Annotate sources", true],
                          ["Draft intro", true],
                          ["Bibliography", false],
                        ].map(([t, done], idx) => (
                          <motion.div
                            key={t}
                            className={styles.wsFakeCheckItem}
                            initial={{ opacity: 0, x: -8 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{
                              delay: 0.85 + idx * 0.1,
                              duration: 0.28,
                            }}
                          >
                            <div
                              className={`${styles.wsFakeCheckBox} ${done ? styles.wsFakeCheckDone : ""}`}
                            >
                              {done && (
                                <FiCheck
                                  size={7}
                                  strokeWidth={3}
                                  color="#fff"
                                />
                              )}
                            </div>
                            <span
                              style={{
                                textDecoration: done ? "line-through" : "none",
                                opacity: done ? 0.4 : 1,
                                fontSize: "9.5px",
                                color: "#14532D",
                              }}
                            >
                              {t}
                            </span>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>

                    {/* ── Counterargument — pink, bottom-left ── */}
                    <motion.div
                      className={styles.wsFakeCard}
                      style={{
                        left: 22,
                        top: 252,
                        width: 226,
                        borderColor: "#DB277744",
                        background: "#FFF0F6",
                      }}
                      initial={{ opacity: 0, y: 16, scale: 0.92 }}
                      whileInView={{ opacity: 1, y: 0, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{
                        delay: 0.74,
                        duration: 0.4,
                        ease: [0.23, 1, 0.32, 1],
                      }}
                    >
                      <div
                        className={styles.wsFakeCardHeader}
                        style={{
                          background: "#DB2777",
                          justifyContent: "space-between",
                        }}
                      >
                        <span>Counterargument</span>
                        <span className={styles.wsAskLumiChip}>
                          <FiZap size={8} /> Ask Lumi
                        </span>
                      </div>
                      <div
                        className={styles.wsFakeCardBody}
                        style={{ color: "#831843" }}
                      >
                        Critics argue reliability is still an issue for
                        renewables.
                      </div>
                    </motion.div>

                    {/* ── Conclusion — green, bottom-right ── */}
                    <motion.div
                      className={styles.wsFakeCard}
                      style={{
                        left: 282,
                        top: 252,
                        width: 290,
                        borderColor: "#16A34A44",
                        background: "#F0FDF4",
                      }}
                      initial={{ opacity: 0, y: 16, scale: 0.92 }}
                      whileInView={{ opacity: 1, y: 0, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{
                        delay: 0.86,
                        duration: 0.4,
                        ease: [0.23, 1, 0.32, 1],
                      }}
                    >
                      <div
                        className={styles.wsFakeCardHeader}
                        style={{ background: "#15803D" }}
                      >
                        Conclusion
                      </div>
                      <div
                        className={styles.wsFakeCardBody}
                        style={{ color: "#14532D" }}
                      >
                        Renewable adoption is inevitable — the question is pace,
                        not direction.
                      </div>
                    </motion.div>

                    {/* Draft with Lumi badge */}
                    <motion.div
                      className={styles.wsDraftBadge}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{
                        delay: 1.5,
                        duration: 0.45,
                        ease: [0.23, 1, 0.32, 1],
                      }}
                    >
                      <FiZap size={11} /> Draft with Lumi
                    </motion.div>
                  </div>
                </div>

                {/* Floating feature labels */}
                <motion.div
                  className={`${styles.wsFloatLabel} ${styles.wsFloatBob}`}
                  style={{ top: 72, left: -18 }}
                  initial={{ opacity: 0, x: -14 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 1.1, duration: 0.45 }}
                >
                  <FiList size={12} /> Checklists
                </motion.div>
                <motion.div
                  className={`${styles.wsFloatLabel} ${styles.wsFloatBob2}`}
                  style={{ top: 28, right: -18 }}
                  initial={{ opacity: 0, x: 14 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 1.2, duration: 0.45 }}
                >
                  <FiClock size={12} /> Deadlines
                </motion.div>
                <motion.div
                  className={`${styles.wsFloatLabel} ${styles.wsFloatBob3}`}
                  style={{ bottom: 28, right: -18 }}
                  initial={{ opacity: 0, x: 14 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 1.35, duration: 0.45 }}
                >
                  <FiZap size={12} /> AI Draft
                </motion.div>
              </motion.div>

              {/* Feature chips */}
              <motion.div className={styles.wsChips} variants={fadeUp}>
                {[
                  [<FiLayout size={13} />, "6 built-in templates"],
                  [<FiCheck size={13} />, "Checklists & Deadlines"],
                  [<FiZap size={13} />, "Ask Lumi on any card"],
                  [<FiCheck size={13} />, "Auto-saves to browser"],
                ].map(([icon, label], i) => (
                  <div key={i} className={styles.wsChip}>
                    <span className={styles.wsChipIcon}>{icon}</span>
                    {label}
                  </div>
                ))}
              </motion.div>

              {/* CTA */}
              <motion.div
                variants={fadeUp}
                style={{ textAlign: "center", marginTop: "2.5rem" }}
              >
                <div
                  className={styles.heroButtons}
                  style={{ justifyContent: "center" }}
                >
                  <button
                    className={`${styles.primaryButton} ${styles.btnAnimateChars}`}
                    onClick={() =>
                      navigate(isAuthenticated ? "/dashboard" : "/auth")
                    }
                  >
                    <span data-button-animate-chars>Open Workspace</span>{" "}
                    <FaArrowRight />
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </section>

          <section
            ref={featuresRef}
            className={`${styles.section} ${styles.heroBg} ${styles.aiSplash} ${styles.sectionFlushTop}`}
            style={{ order: 1 }}
          >
            <div className={styles.aiOrb1} />
            <div className={styles.aiOrb2} />
            <motion.div
              className={styles.aiInner}
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
            >
              {/* Header */}
              <motion.div
                className={styles.aiHeader}
                variants={staggerContainer}
              >
                <motion.h2
                  id="featuresTitle"
                  className={styles.aiHeadline}
                  variants={clipReveal}
                >
                  {"Every Tool Students Need,"
                    .split(" ")
                    .map((word, i, arr) => (
                      <motion.span
                        key={i}
                        variants={wordReveal}
                        style={{ display: "inline-block" }}
                      >
                        {word}
                        {i < arr.length - 1 ? "\u00A0" : ""}
                      </motion.span>
                    ))}{" "}
                  <span className={styles.aiAccent}>Built In.</span>
                </motion.h2>
                <motion.p className={styles.aiSubtitle} variants={fadeUp}>
                  Your notes, deadlines, essays, and mind maps — all on one
                  drag-and-drop canvas. No switching tabs, no lost context.
                </motion.p>
              </motion.div>

              {/* ── Hero: 3-pane workspace demo ───────────────────────────── */}
              <motion.div
                className={styles.workspaceHeroDemo}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
              >
                <motion.div
                  className={styles.workspaceHeroCaption}
                  variants={staggerContainer}
                  initial="hidden"
                  whileInView="visible"
                  viewport={viewport}
                >
                  <motion.span
                    className={styles.workspaceHeroKicker}
                    variants={fadeUp}
                  >
                    <span className={styles.workspaceHeroKickerDot} />
                    One connected workspace
                  </motion.span>
                  <motion.h3
                    className={styles.workspaceHeroTitle}
                    variants={fadeUp}
                  >
                    Notes, quizzes, essays,{" "}
                    <span className={styles.workspaceHeroAccent}>
                      all together
                    </span>
                    <br className={styles.workspaceHeroBr} /> in one screen.
                  </motion.h3>
                  <motion.p
                    className={styles.workspaceHeroSub}
                    variants={fadeUp}
                  >
                    Capture notes, generate practice questions, chat with Lumi,
                    and draft assignments without losing context between tools.
                  </motion.p>
                </motion.div>
                <CapConversionDemo />
              </motion.div>

              {/* ── Unified "One app, three tools" card ────────────── */}
              <motion.div
                className={styles.triApp}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
                onMouseEnter={() => setTriAppPaused(true)}
                onMouseLeave={() => setTriAppPaused(false)}
              >
                <div className={styles.triAppHdr}>
                  <span className={styles.triAppEyebrow}>
                    No more tab-hopping
                  </span>
                  <h3 className={styles.triAppTitle}>
                    One app. Three tools. <em>Actually</em> connected.
                  </h3>
                  <p className={styles.triAppLede}>
                    Your notes know what's in your essays. Your quizzes know
                    what's in your notes. When your tools talk to each other,
                    you stop losing context every time you switch tabs.
                  </p>
                </div>

                <div className={styles.triAppGrid}>
                  <TriAppList
                    active={triAppActive}
                    onSetActive={setTriAppActive}
                  />

                  {/* Cycling visual panel */}
                  <div className={styles.triAppVisual}>
                    <div className={styles.triAppStage}>
                      <AnimatePresence initial={false}>
                        <motion.div
                          key={triAppActive}
                          className={styles.triAppVisualInner}
                          initial={{
                            opacity: 0,
                            scale: 1.015,
                            filter: "blur(6px)",
                          }}
                          animate={{
                            opacity: 1,
                            scale: 1,
                            filter: "blur(0px)",
                          }}
                          exit={{
                            opacity: 0,
                            scale: 0.99,
                            filter: "blur(4px)",
                          }}
                          transition={{
                            opacity: { duration: 0.7, ease: [0.4, 0, 0.2, 1] },
                            scale: { duration: 0.9, ease: [0.23, 1, 0.32, 1] },
                            filter: { duration: 0.7, ease: [0.4, 0, 0.2, 1] },
                          }}
                        >
                          {triAppActive === 0 && <MiniChatDemo />}
                          {triAppActive === 1 && (
                            <div className={styles.triAppUploadDemo}>
                              <div className={styles.triAppUploadHdr}>
                                <span className={styles.triAppUploadTitle}>
                                  3 files indexed
                                </span>
                                <span className={styles.triAppUploadBadge}>
                                  Ready to chat
                                </span>
                              </div>
                              {[
                                {
                                  name: "Lecture_Notes.pdf",
                                  meta: "2.4 MB · 24 p",
                                  done: true,
                                },
                                {
                                  name: "Chapter_7.docx",
                                  meta: "1.1 MB · 18 p",
                                  done: true,
                                },
                                {
                                  name: "Recording.mp3",
                                  meta: "18 MB · 42 min",
                                  done: false,
                                },
                              ].map((f) => (
                                <div
                                  key={f.name}
                                  className={styles.triAppUploadRow}
                                >
                                  <span className={styles.triAppUploadDot}>
                                    {f.done ? "✓" : "…"}
                                  </span>
                                  <span className={styles.triAppUploadName}>
                                    {f.name}
                                  </span>
                                  <span className={styles.triAppUploadMeta}>
                                    {f.meta}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                          {triAppActive === 2 && <CommandPaletteDemo />}
                        </motion.div>
                      </AnimatePresence>
                    </div>

                    {/* Pager bars — active one fills over the cycle duration */}
                    <div className={styles.triAppDots} aria-hidden="true">
                      {[0, 1, 2].map((i) => {
                        const state =
                          i < triAppActive
                            ? "done"
                            : i === triAppActive
                              ? "active"
                              : "idle";
                        return (
                          <span
                            key={i}
                            className={`${styles.triAppDot} ${
                              state === "active" ? styles.triAppDotActive : ""
                            } ${state === "done" ? styles.triAppDotDone : ""}`}
                          >
                            <span
                              className={styles.triAppDotFill}
                              style={{
                                animationPlayState: triAppPaused
                                  ? "paused"
                                  : "running",
                                animationDuration:
                                  state === "active" ? "5.2s" : "0s",
                              }}
                              key={`${triAppActive}-${i}-${triAppPaused}`}
                            />
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Savings comparison strip */}
              <SavingsStrip />
            </motion.div>
          </section>

          {/* ── Flow state: Spotify in the workspace ─────────────────────── */}
          <section
            className={`${styles.section} ${styles.heroBg} ${styles.flowSection}`}
            style={{ order: 5 }}
          >
            <div className={styles.flowOrb1} />
            <div className={styles.flowOrb2} />

            <motion.div
              className={styles.flowInner}
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
            >
              <motion.div className={styles.flowGrid}>
                <motion.div
                  className={styles.flowCopy}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
                >
                  <span className={styles.flowKicker}>
                    <span className={styles.flowKickerDot} />
                    Included · Spotify in your workspace
                  </span>
                  <h2 className={styles.flowHeadline}>
                    Never leave the workspace.{" "}
                    <span className={styles.flowAccent}>
                      Not even for music.
                    </span>
                  </h2>
                  <p className={styles.flowSubtitle}>
                    Every tab switch is a focus tax. Bramlo keeps Spotify on the
                    same surface as your notes, PDFs, session timer, and Lumi —
                    skip tracks, queue what&apos;s next, and dial volume without
                    touching another window. Your study loop stays one screen
                    deep.
                  </p>
                  <FlowFeaturesList />
                  <div className={styles.flowJoinWaitlist}>
                    <span className={styles.flowJoinDot} />
                    <span className={styles.flowJoinText}>
                      Connect Spotify once in Settings — playback stays inside
                      your session
                    </span>
                  </div>
                </motion.div>

                <motion.div
                  className={styles.flowDemo}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.5,
                    ease: [0.23, 1, 0.32, 1],
                    delay: 0.15,
                  }}
                >
                  <FocusSpotifyDemo />
                </motion.div>
              </motion.div>
            </motion.div>
          </section>

          {/* ── Powerful Capabilities ─────────────────────────────────────── */}
          <section
            className={`${styles.section} ${styles.heroBg} ${styles.capSplash}`}
            style={{ order: 6 }}
          >
            <div className={styles.capOrb} />
            <motion.div
              className={styles.capInner}
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
            >
              <motion.div
                className={styles.capHeader}
                variants={staggerContainer}
              >
                <motion.h2 className={styles.capHeadline} variants={clipReveal}>
                  {"Every Tool Students Need,"
                    .split(" ")
                    .map((word, i, arr) => (
                      <motion.span
                        key={i}
                        variants={wordReveal}
                        style={{ display: "inline-block" }}
                      >
                        {word}
                        {i < arr.length - 1 ? "\u00A0" : ""}
                      </motion.span>
                    ))}{" "}
                  <span className={styles.capAccent}>Built In.</span>
                </motion.h2>
                <motion.p className={styles.capSubtitle} variants={fadeUp}>
                  Your notes, deadlines, essays, and mind maps — all on one
                  drag-and-drop canvas. No switching tabs, no lost context.
                </motion.p>
              </motion.div>

              <div className={styles.capTrio}>
                {capabilities.map((cap, i) => (
                  <motion.div
                    key={cap.title}
                    className={styles.capTrioCard}
                    style={{
                      "--cap-col": ["#6366f1", "#0ea5e9", "#a855f7"][i],
                    }}
                    initial={{ opacity: 0, y: 44 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: i * 0.15,
                      duration: 0.5,
                      ease: [0.23, 1, 0.32, 1],
                    }}
                  >
                    <div className={styles.capCardBar} />
                    <div className={styles.capTrioGlow} />
                    <div className={styles.capTrioTop}>
                      <div className={styles.capTrioIconWrap}>{cap.icon}</div>
                      <span className={styles.capTrioIdx}>
                        {["01", "02", "03"][i]}
                      </span>
                    </div>
                    <h3 className={styles.capTrioTitle}>{cap.title}</h3>
                    <p className={styles.capTrioDesc}>{cap.description}</p>
                    <ul className={styles.capTrioList}>
                      {cap.features.map((f, fi) => (
                        <li key={fi}>
                          <FiCheck size={11} />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <div className={styles.capTrioFoot}>
                      {i === 0 && <ChatCardFooter />}
                      {i === 1 && <NotesCardFooter />}
                      {i === 2 && <QuizCardFooter />}
                    </div>
                  </motion.div>
                ))}
              </div>
              <AnimatePresence mode="wait" />
            </motion.div>
          </section>

          {/* ── Assignment Automation ────────────────────────────────────── */}
          <section
            className={`${styles.section} ${styles.automationSection} ${styles.sectionBg2}`}
            style={{ order: 2 }}
          >
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
            >
              <motion.div
                className={styles.autoHdrBlock}
                variants={staggerContainer}
              >
                <motion.h2
                  className={styles.autoHeadline}
                  variants={clipReveal}
                >
                  {"Assignments, on".split(" ").map((word, i, arr) => (
                    <motion.span
                      key={i}
                      variants={wordReveal}
                      style={{ display: "inline-block" }}
                    >
                      {word}
                      {i < arr.length - 1 ? "\u00A0" : ""}
                    </motion.span>
                  ))}{" "}
                  <span className={styles.autoAccent}>autopilot.</span>
                </motion.h2>
                <motion.p className={styles.autoSubtitle} variants={fadeUp}>
                  You bring the requirements and your sources. Bramlo drafts,
                  cites, and formats — while you stay in control of the ideas.
                </motion.p>
              </motion.div>

              <AutoFeaturesGrid />

              <motion.div
                className={styles.automationLiveDemo}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
              >
                <EssayWritingDemo />
              </motion.div>

              <motion.div
                className={styles.automationFlowContainer}
                variants={clipRevealBlock}
              >
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  nodeTypes={nodeTypes}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  nodesDraggable={false}
                  nodesConnectable={false}
                  elementsSelectable={false}
                  panOnDrag={false}
                  zoomOnScroll={false}
                  zoomOnPinch={false}
                  zoomOnDoubleClick={false}
                  preventScrolling={false}
                  fitView
                  defaultEdgeOptions={{
                    animated: true,
                    style: { strokeWidth: 3, strokeOpacity: 1 },
                  }}
                  attributionPosition="bottom-right"
                  proOptions={{ hideAttribution: true }}
                  style={{
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                  }}
                >
                  <Background
                    color="rgba(255, 255, 255, 0.03)"
                    gap={20}
                    size={1}
                    style={{ opacity: 0.3 }}
                  />
                </ReactFlow>
              </motion.div>
            </motion.div>
          </section>
        </div>

        {/* ── Demo ─────────────────────────────────────────────────────────── */}
        <section
          ref={demoRef}
          className={`${styles.section} ${styles.sectionBg4}`}
        >
          <motion.div
            className={styles.demoSection}
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
          >
            <motion.div className={styles.demoPreview} variants={fadeUp}>
              <div className={styles.demoScrollContainer}>
                {showDemo && <ModernDemo />}
              </div>
            </motion.div>
          </motion.div>
        </section>

        {/* ── Student Success Stories ───────────────────────────────────── */}
        <section
          className={`${styles.section} ${styles.heroBg} ${styles.statsSplash}`}
        >
          <div className={styles.statsOrb1} />
          <div className={styles.statsOrb2} />
          <motion.div
            className={styles.statsInner}
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
          >
            <motion.div
              className={styles.statsHdrBlock}
              variants={staggerContainer}
            >
              <motion.h2
                className={styles.statsHeadline2}
                variants={clipReveal}
              >
                {"Real students.".split(" ").map((word, i, arr) => (
                  <motion.span
                    key={i}
                    variants={wordReveal}
                    style={{ display: "inline-block" }}
                  >
                    {word}
                    {i < arr.length - 1 ? "\u00A0" : ""}
                  </motion.span>
                ))}{" "}
                <span className={styles.statsAccent2}>Real results.</span>
              </motion.h2>
              <motion.p className={styles.statsSubline} variants={fadeUp}>
                Over 297,000 students chose a better way to study. Here's what
                changed.
              </motion.p>
            </motion.div>
            <div className={styles.statsEditorial}>
              <motion.div className={styles.statsFeatured} variants={boxReveal}>
                <div className={styles.statsBgWatermark}>{stats[0].value}</div>
                <StatCounter
                  value={stats[0].value}
                  className={styles.statsFeaturedNum}
                />
                <div className={styles.statsFeaturedLbl}>{stats[0].label}</div>
                <p className={styles.statsFeaturedDsc}>
                  {stats[0].description}
                </p>
              </motion.div>
              <div className={styles.statsSideCol}>
                {stats.slice(1).map((stat, i) => (
                  <motion.div
                    key={stat.label}
                    className={styles.statsSideItem}
                    style={{ "--side-col": ["#10b981", "#a855f7"][i] }}
                    initial={{
                      opacity: 0,
                      x: 40,
                      filter: "blur(10px)",
                      scale: 0.96,
                    }}
                    whileInView={{
                      opacity: 1,
                      x: 0,
                      filter: "blur(0px)",
                      scale: 1,
                    }}
                    viewport={{ once: true }}
                    transition={{
                      delay: 0.2 + i * 0.15,
                      duration: 0.68,
                      ease: easeSnap,
                      filter: { duration: 0.56, ease: easeSnap },
                    }}
                  >
                    <div className={styles.statsSideAccentLine} />
                    <StatCounter
                      value={stat.value}
                      className={styles.statsSideNum}
                    />
                    <div className={styles.statsSideLbl}>{stat.label}</div>
                    <p className={styles.statsSideDsc}>{stat.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </section>

        {/* ── What Students Say ─────────────────────────────────────────── */}
        <section
          className={`${styles.section} ${styles.heroBg} ${styles.reviewSplash}`}
        >
          <div className={styles.reviewOrb} />
          <motion.div
            className={styles.reviewInner}
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
          >
            <motion.div
              className={styles.reviewHdr}
              variants={staggerContainer}
            >
              <motion.h2
                className={styles.reviewHeadline}
                variants={clipReveal}
              >
                {"From students".split(" ").map((word, i, arr) => (
                  <motion.span
                    key={i}
                    variants={wordReveal}
                    style={{ display: "inline-block" }}
                  >
                    {word}
                    {i < arr.length - 1 ? "\u00A0" : ""}
                  </motion.span>
                ))}{" "}
                <span className={styles.reviewAccent}>exactly like you.</span>
              </motion.h2>
              <motion.div
                className={styles.reviewRateSummary}
                variants={fadeUp}
              >
                <span className={styles.reviewStars}>{"★★★★★"}</span>
                <span className={styles.reviewRateNum}>4.9 / 5</span>
                <span className={styles.reviewRateSep}>·</span>
                <span className={styles.reviewRateDesc}>1,000+ students</span>
              </motion.div>
              <motion.p className={styles.reviewSubtitle} variants={fadeUp}>
                They weren't failing. They just needed a better system — and
                once they had one, everything changed.
              </motion.p>
            </motion.div>
            <TestimonialsGrid />
          </motion.div>
        </section>

        {/* ── Why Not Just Use ChatGPT? — Comparison Table ──────────────── */}
        <section className={`${styles.section} ${styles.compareSection}`}>
          <motion.div
            className={styles.compareInner}
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
          >
            <motion.div
              className={styles.compareHdr}
              variants={staggerContainer}
            >
              <motion.h2
                className={styles.compareHeadline}
                variants={clipReveal}
              >
                Why not just use{" "}
                <span className={styles.compareStrike}>ChatGPT</span>?
              </motion.h2>
              <motion.p className={styles.compareSub} variants={fadeUp}>
                Because ChatGPT doesn&rsquo;t read your syllabus. Notion
                doesn&rsquo;t write essays. Quizlet can&rsquo;t summarize a
                lecture. Lumi does all three — grounded in your actual
                coursework.
              </motion.p>
              <motion.p className={styles.compareSubStrong} variants={fadeUp}>
                One workspace. One subscription. Everything tied to your real
                class materials.
              </motion.p>
            </motion.div>

            <motion.div
              className={styles.compareTableWrap}
              variants={boxReveal}
            >
              <table className={styles.compareTable}>
                <thead>
                  <tr>
                    <th className={styles.compareColLabel}>&nbsp;</th>
                    <th className={styles.compareColUs}>
                      <span className={styles.compareBrand}>Lumi</span>
                      <span className={styles.compareBrandPrice}>$9.99/mo</span>
                    </th>
                    <th className={styles.compareColThem}>
                      <span className={styles.compareCompetitorName}>
                        ChatGPT Plus
                      </span>
                      <span className={styles.compareCompetitorPrice}>
                        $20/mo
                      </span>
                    </th>
                    <th className={styles.compareColThem}>
                      <span className={styles.compareCompetitorName}>
                        Notion AI
                      </span>
                      <span className={styles.compareCompetitorPrice}>
                        $10 + $10/mo
                      </span>
                    </th>
                    <th className={styles.compareColThem}>
                      <span className={styles.compareCompetitorName}>
                        Quizlet Plus
                      </span>
                      <span className={styles.compareCompetitorPrice}>
                        $35.99/yr
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      feat: "Grounded in YOUR PDFs & notes",
                      us: "yes",
                      gpt: "no",
                      notion: "limited",
                      quizlet: "no",
                    },
                    {
                      feat: "Unlimited PDF uploads",
                      us: "yes",
                      gpt: "8 files · 50MB",
                      notion: "no",
                      quizlet: "no",
                    },
                    {
                      feat: "Auto-generate flashcards",
                      us: "yes",
                      gpt: "manual prompt",
                      notion: "no",
                      quizlet: "manual",
                    },
                    {
                      feat: "Auto-generate quizzes",
                      us: "yes",
                      gpt: "manual prompt",
                      notion: "no",
                      quizlet: "limited",
                    },
                    {
                      feat: "Essay writer with live citations",
                      us: "yes",
                      gpt: "hallucinates sources",
                      notion: "no",
                      quizlet: "no",
                    },
                    {
                      feat: "Mind maps & infinite canvas",
                      us: "yes",
                      gpt: "no",
                      notion: "no",
                      quizlet: "no",
                    },
                    {
                      feat: "Side-by-side PDF + notes workspace",
                      us: "yes",
                      gpt: "no",
                      notion: "no",
                      quizlet: "no",
                    },
                    {
                      feat: "Built for students",
                      us: "yes",
                      gpt: "no",
                      notion: "no",
                      quizlet: "yes",
                    },
                  ].map((row) => (
                    <tr key={row.feat}>
                      <td className={styles.compareRowLabel}>{row.feat}</td>
                      <td
                        className={`${styles.compareCell} ${styles.compareCellUs}`}
                      >
                        {row.us === "yes" ? (
                          <span className={styles.compareCheck}>
                            <FiCheck size={14} />
                          </span>
                        ) : (
                          <span className={styles.compareCellText}>
                            {row.us}
                          </span>
                        )}
                      </td>
                      {[row.gpt, row.notion, row.quizlet].map((v, i) => (
                        <td key={i} className={styles.compareCell}>
                          {v === "yes" ? (
                            <span className={styles.compareCheckMuted}>
                              <FiCheck size={14} />
                            </span>
                          ) : v === "no" ? (
                            <span className={styles.compareX}>
                              <FiX size={14} />
                            </span>
                          ) : (
                            <span className={styles.compareCellText}>{v}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>

            <motion.div className={styles.compareFootNote} variants={fadeUp}>
              <span className={styles.compareFootDot} />
              Replace 3 subscriptions ($65+/mo combined) with one app built for
              how students actually learn.
            </motion.div>
          </motion.div>
        </section>

        {/* ── "Or... do nothing" — the real alternative ─────────────── */}
        <section className={styles.vsNothing}>
          <motion.div
            className={styles.vsNothingInner}
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
          >
            <motion.div className={styles.vsNothingHdr} variants={fadeUp}>
              <h2 className={styles.vsNothingHeadline}>
                The real competition isn&rsquo;t another app.{" "}
                <span className={styles.vsNothingAccent}>
                  It&rsquo;s doing nothing.
                </span>
              </h2>
              <p className={styles.vsNothingSub}>
                Most students know they need a better system. They keep putting
                it off. Here's what that decision actually costs.
              </p>
              <p className={styles.vsNothingSubStrong}>
                Stop waiting for it to click on its own.
              </p>
            </motion.div>

            <motion.div
              className={styles.vsNothingGrid}
              variants={staggerContainer}
            >
              <motion.div
                className={`${styles.vsNothingCol} ${styles.vsNothingColBad}`}
                variants={boxReveal}
              >
                <div className={styles.vsNothingColHdr}>
                  <span className={styles.vsNothingColTag}>Without Lumi</span>
                  <span className={styles.vsNothingColSub}>
                    The familiar spiral
                  </span>
                </div>
                <ul className={styles.vsNothingList}>
                  {[
                    {
                      metric: "10+ hours",
                      label: "re-reading what you already read",
                    },
                    {
                      metric: "$20",
                      label: "in coffee that just made the anxiety worse",
                    },
                    {
                      metric: "4 apps open",
                      label: "none of them know your material",
                    },
                    {
                      metric: "0 citations",
                      label: "you can actually trace back",
                    },
                    {
                      metric: "C+ grade",
                      label: "after the hardest week of your semester",
                    },
                    {
                      metric: "Brain fog",
                      label: "through your next three classes",
                    },
                  ].map((row) => (
                    <li key={row.metric} className={styles.vsNothingRow}>
                      <span className={styles.vsNothingRowIcon}>
                        <FiX size={13} />
                      </span>
                      <span className={styles.vsNothingRowMetric}>
                        {row.metric}
                      </span>
                      <span className={styles.vsNothingRowLabel}>
                        {row.label}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className={styles.vsNothingColFoot}>
                  Total cost of one midterm
                  <strong className={styles.vsNothingColTotal}>
                    ~$75 &amp; your weekend
                  </strong>
                </div>
              </motion.div>

              <motion.div className={styles.vsNothingVs} variants={fadeUp}>
                <span>vs.</span>
              </motion.div>

              <motion.div
                className={`${styles.vsNothingCol} ${styles.vsNothingColGood}`}
                variants={boxReveal}
              >
                <div className={styles.vsNothingColHdr}>
                  <span className={styles.vsNothingColTag}>With Lumi</span>
                  <span className={styles.vsNothingColSub}>
                    The system that actually works
                  </span>
                </div>
                <ul className={styles.vsNothingList}>
                  {[
                    {
                      metric: "30 min setup",
                      label: "drop in your notes, PDFs, and lectures",
                    },
                    {
                      metric: "$0.33/day",
                      label: "everything included, no add-ons",
                    },
                    {
                      metric: "1 workspace",
                      label: "notes, PDFs, AI, flashcards — all connected",
                    },
                    {
                      metric: "Every answer",
                      label: "traced back to your own material",
                    },
                    {
                      metric: "B+ to A",
                      label: "because active recall actually works",
                    },
                    {
                      metric: "Your weekend",
                      label: "back",
                    },
                  ].map((row) => (
                    <li key={row.metric} className={styles.vsNothingRow}>
                      <span
                        className={`${styles.vsNothingRowIcon} ${styles.vsNothingRowIconGood}`}
                      >
                        <FiCheck size={13} />
                      </span>
                      <span className={styles.vsNothingRowMetric}>
                        {row.metric}
                      </span>
                      <span className={styles.vsNothingRowLabel}>
                        {row.label}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className={styles.vsNothingColFoot}>
                  Total cost of one midterm
                  <strong className={styles.vsNothingColTotal}>
                    $9.99 &amp; your weekend back
                  </strong>
                </div>
              </motion.div>
            </motion.div>

            <motion.div className={styles.vsNothingCta} variants={fadeUp}>
              <a
                href={foundingOfferLink}
                target="_blank"
                rel="noopener noreferrer"
                className={`${styles.primaryButton} ${styles.goldButton}`}
                onClick={(e) => {
                  e.preventDefault();
                  handlePlanClick("monthly");
                }}
              >
                Claim $9.99 Student Plan <FiArrowRight size={14} />
              </a>
              <span className={styles.vsNothingCtaNote}>
                Next 500 students · spots filling fast
              </span>
            </motion.div>
          </motion.div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────────────── */}
        <section
          className={`${styles.section} ${styles.faqSection} ${styles.heroBg} ${styles.faqSplash}`}
        >
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
          >
            <div className={styles.faqSplitWrap}>
              <motion.div className={styles.faqSplitLeft} variants={fadeUp}>
                <div className={styles.faqEyebrow}>FAQ</div>
                <h2 className={styles.faqSplashHead}>
                  Still on
                  <br />
                  <span className={styles.faqSplashAccent}>the fence?</span>
                </h2>
                <p className={styles.faqSplashSub}>
                  Honest answers to the questions you're probably thinking right
                  now.
                </p>
                <div className={styles.faqDecorLine} />
              </motion.div>
              <div className={styles.faqSplitRight}>
                <div className={styles.faqBox}>
                  {faq.map((faqItem, index) => {
                    const isOpen = openFaqIndex === index;
                    return (
                      <motion.div
                        key={index}
                        className={`${styles.valueCard} ${styles.faqCard} ${styles.shellReset}`}
                        variants={clipRevealBlock}
                      >
                        <button
                          className={styles.faqHeader}
                          onClick={() =>
                            setOpenFaqIndex((prev) =>
                              prev === index ? null : index,
                            )
                          }
                          aria-expanded={isOpen}
                          aria-controls={`faq-content-${index}`}
                        >
                          <span className={styles.faqBadge}>{index + 1}</span>
                          <span className={styles.faqQuestion}>
                            {faqItem.question}
                          </span>
                        </button>
                        <div
                          id={`faq-content-${index}`}
                          className={`${styles.faqContent} ${isOpen ? styles.open : ""}`}
                          ref={(el) => (faqContentRefs.current[index] = el)}
                          style={{
                            maxHeight:
                              isOpen && faqContentRefs.current[index]
                                ? faqContentRefs.current[index].scrollHeight +
                                  "px"
                                : 0,
                          }}
                        >
                          <div className={styles.faqBody}>
                            <p className={styles.faqAnswer}>{faqItem.answer}</p>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        <section
          ref={pricingRef}
          id="pricing"
          className={`${styles.section} ${styles.heroBg}`}
        >
          <motion.div
            className={styles.pricingInner}
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
          >
            <motion.div
              className={styles.pricingHdrBlock}
              variants={staggerContainer}
            >
              <motion.h2
                className={styles.pricingHeadline}
                variants={clipReveal}
              >
                {"Simple,".split(" ").map((word, i, arr) => (
                  <motion.span
                    key={i}
                    variants={wordReveal}
                    style={{ display: "inline-block" }}
                  >
                    {word}
                    {i < arr.length - 1 ? "\u00A0" : ""}
                  </motion.span>
                ))}{" "}
                <span className={styles.pricingAccent}>Honest Pricing</span>
              </motion.h2>
              <motion.p className={styles.pricingSubline} variants={fadeUp}>
                Next 500 students can lock in the $9.99/month student rate —
                cancel anytime.
              </motion.p>
              <motion.div
                className={styles.pricingTrustPills}
                variants={fadeUp}
              >
                {[
                  "Save 58% annually",
                  "Next 500 students only",
                  "$9.99/mo student rate",
                  "Cancel anytime",
                  "All features included",
                ].map((pill) => (
                  <span key={pill} className={styles.pricingTrustPill}>
                    {pill}
                  </span>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              className={styles.pricingTiers}
              style={{ justifyContent: "center" }}
              variants={staggerContainer}
            >
              {/* Monthly Plan */}
              <motion.div
                className={styles.pricingTierLink}
                variants={boxReveal}
                onClick={() => handlePlanClick("monthly")}
                style={{ cursor: "pointer" }}
              >
                <div className={`${styles.pricingTier} ${styles.shellReset}`}>
                  <div className={styles.saveBadge}>
                    SPECIAL STUDENT RATE · NEXT 500
                  </div>
                  <div className={styles.pricingCardInner}>
                    <span className={styles.tierName}>Bramlo Student Plan</span>
                    <div className={styles.tierPrice}>
                      <span className={styles.price}>$9.99</span>
                      <span className={styles.period}>/month</span>
                    </div>
                    <span className={styles.tierDescription}>
                      Special student pricing for the next 500 spots. Cancel
                      anytime.
                    </span>
                    <div
                      className={`${styles.planAction} ${styles.btnAnimateChars}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlanClick("monthly");
                      }}
                    >
                      <span data-button-animate-chars>Claim Student Plan</span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Annual Plan — highlighted */}
              <motion.div
                className={styles.pricingTierLink}
                variants={boxReveal}
                onClick={() => handlePlanClick("annual")}
                style={{ cursor: "pointer" }}
              >
                <div
                  className={`${styles.pricingTier} ${styles.highlighted} ${styles.shellReset}`}
                >
                  <div className={styles.saveBadge}>BEST VALUE</div>
                  <div className={styles.pricingCardInner}>
                    <span className={styles.tierName}>Bramlo Pro Annual</span>
                    <div className={styles.tierPrice}>
                      <span className={styles.price}>$49.99</span>
                      <span className={styles.period}>/year</span>
                    </div>
                    <span className={styles.tierDescription}>
                      ~$4.17/mo billed annually. Save 58% vs monthly. Cancel
                      anytime.
                    </span>
                    <div
                      className={`${styles.planAction} ${styles.btnAnimateChars}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlanClick("annual");
                      }}
                    >
                      <span data-button-animate-chars>Get Annual Plan</span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Enterprise Plan */}
              <MotionLink
                to="/contact"
                className={styles.pricingTierLink}
                variants={boxReveal}
              >
                <div className={`${styles.pricingTier} ${styles.shellReset}`}>
                  <div className={styles.pricingCardInner}>
                    <span className={styles.tierName}>Enterprise</span>
                    <div className={styles.tierPrice}>
                      <span className={styles.price}>Custom</span>
                    </div>
                    <span className={styles.tierDescription}>
                      Tailored for schools & organizations
                    </span>
                    <div
                      className={`${styles.planAction} ${styles.btnAnimateChars}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/contact");
                      }}
                    >
                      <span data-button-animate-chars>Contact Sales</span>
                    </div>
                  </div>
                </div>
              </MotionLink>
            </motion.div>

            <motion.p className={styles.pricingReassurance} variants={fadeUp}>
              Next 500 spots at $9.99/mo · Save 58% annually · Cancel anytime
            </motion.p>
          </motion.div>
        </section>

        {/* ── Sticky bottom conversion CTA ───────────────────────────── */}
        <div
          className={`${styles.stickyCta} ${
            stickyCtaOpen ? styles.stickyCtaVisible : ""
          }`}
          aria-hidden={!stickyCtaOpen}
        >
          <div className={styles.stickyCtaInner}>
            <div className={styles.stickyCtaAvatars}>
              {["#818cf8", "#34d399", "#f97316"].map((c, i) => (
                <span
                  key={i}
                  className={styles.stickyCtaAvatar}
                  style={{
                    background: c,
                    marginLeft: i > 0 ? "-7px" : 0,
                    zIndex: 3 - i,
                  }}
                />
              ))}
            </div>
            <div className={styles.stickyCtaText}>
              <strong>Still here? That means something.</strong>
              <span className={styles.stickyCtaSub}>
                Join 297,000+ students who stopped struggling and started
                actually learning.
              </span>
            </div>
            <a
              href={foundingOfferLink}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.stickyCtaBtn}
              onClick={(e) => {
                e.preventDefault();
                handlePlanClick("monthly");
              }}
            >
              Claim $9.99 plan <FiArrowRight size={14} />
            </a>
            <button
              type="button"
              className={styles.stickyCtaClose}
              onClick={dismissStickyCta}
              aria-label="Dismiss"
            >
              <FiX size={14} />
            </button>
          </div>
        </div>

        {/* Scroll-to-top control with progress ring */}
        <button
          type="button"
          className={`${styles.scrollTopButton} ${
            showScrollTop ? styles.scrollTopVisible : ""
          }`}
          style={{ "--p": `${scrollProgress}%` }}
          aria-label="Scroll to top"
          onClick={handleScrollTop}
        >
          <span className={styles.scrollTopInner}>
            {/* Up arrow icon */}
            <svg
              className={styles.scrollTopIcon}
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M7 14l5-5 5 5H7z" />
            </svg>
          </span>
        </button>
      </div>
    </div>
  );
};

export default LandingPage;
